import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fetchHomepageConfig, readHomepageConfigCacheSync } from "@/lib/homepageConfigCache";
import { useI18n } from "@/lib/i18n";
import { AlphaVideo } from "@/components/AlphaVideo";
import {
  School, FileText, Gamepad2, BarChart3, Award, Trophy,
  Clock, Bot, Users, Globe, BookOpen, ChevronDown,
  Instagram, Youtube, Facebook, ChevronRight, Laptop, Spade,
  Mail, MapPin, Phone, Sparkles, MessageCircle, Home as HomeIcon,
  LogOut, Zap, Star, Crown, Medal, Flame, Heart, ThumbsUp,
  Rocket, Target, Gem, Diamond, Timer, Play, ExternalLink, ShoppingCart, GraduationCap,
} from "lucide-react";

/** Icon registry — names in `homepage_config` map to lucide components. */
const ICONS = {
  School, FileText, Gamepad2, BarChart3, Award, Trophy,
  Clock, Bot, Users, Globe, BookOpen, Mail, MessageCircle,
  Home: HomeIcon, LogOut, Timer,
  Sparkles, Star, Crown, Medal, Zap, Flame, Heart, ThumbsUp,
  Rocket, Target, Gem, Diamond,
};

/** Curated palette mapped from `color` strings to Tailwind class bundles.
 *  Tuned to match the vibrant lime/green of the SUPERTMATIK logo + kid-
 *  friendly vivid hues. Each entry exposes button + (where relevant) ring,
 *  text and bg colors used by the cards / phase bubbles. */
const PALETTE = {
  // CTA primaries — bold and chunky
  amber:  { btn: "bg-amber-400 hover:bg-amber-500 text-indigo-950 ring-2 ring-amber-300/40", icon: "text-indigo-950" },
  white:  { btn: "bg-white hover:bg-lime-50 text-blue-600 border-2 border-lime-400 hover:border-lime-500", icon: "text-lime-600" },
  indigo: { btn: "bg-gradient-to-br from-lime-400 to-lime-500 hover:from-lime-500 hover:to-lime-600 text-indigo-950 ring-2 ring-lime-300/50", icon: "text-indigo-950" },
  // Online mode cards — vibrant per-mode hue
  blue:   { btn: "bg-gradient-to-br from-sky-400 to-blue-600 hover:from-sky-500 hover:to-blue-700 text-white", ring: "ring-blue-400/40", txt: "text-blue-600", bg: "bg-blue-50" },
  green:  { btn: "bg-gradient-to-br from-lime-400 to-emerald-500 hover:from-lime-500 hover:to-emerald-600 text-white", ring: "ring-emerald-400/40", txt: "text-emerald-600", bg: "bg-emerald-50" },
  violet: { btn: "bg-gradient-to-br from-fuchsia-400 to-violet-600 hover:from-fuchsia-500 hover:to-violet-700 text-white", ring: "ring-violet-400/40", txt: "text-violet-600", bg: "bg-violet-50" },
  // Phase numbered circles — chunky drop-shadow
  emerald: { ring: "bg-gradient-to-br from-lime-400 to-emerald-500 shadow-emerald-400/40" },
  orange:  { ring: "bg-gradient-to-br from-amber-400 to-orange-500 shadow-orange-400/40" },
  sky:     { ring: "bg-gradient-to-br from-sky-400 to-blue-500 shadow-blue-400/40" },
};

/** Resolve a {field}_pt/en/es value with PT fallback when the active lang is empty. */
const pick = (obj, field, lang) => obj?.[`${field}_${lang}`] || obj?.[`${field}_pt`] || "";

/**
 * PhaseIcon — renders a phase's illustration in the "AS FASES DO CONCURSO"
 * strip. Priority order per phase:
 *   1) `phase.icon_url` (or `phase[altKey]`) → uploaded image
 *   2) `phase.emoji_override` (or `phase[altEmoji]`) → custom emoji character
 *   3) `defaultRender()` → arbitrary component (used for the FannedCards slot)
 *   4) `defaultEmoji` → static fallback emoji
 */
function PhaseIcon({ phase, size, fallbackNode, defaultEmoji, glow = "rgba(0,0,0,0.4)", altKey = "icon_url", altEmoji = "emoji_override" }) {
  if (!phase) return null;
  const url = phase[altKey] || phase.icon_url;
  const emoji = phase[altEmoji] || phase.emoji_override;
  // Circle-mask preset — applies border-radius + optional background/ring so
  // the uploaded image looks like the Dark/Neon Gaming reference without
  // needing to pre-crop a rounded PNG.
  const mask = phase.icon_mask || "none";
  const maskStyle = (() => {
    switch (mask) {
      case "circle":
        return { borderRadius: "50%", overflow: "hidden" };
      case "rounded":
        return { borderRadius: "22%", overflow: "hidden" };
      case "circle_neon":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: `0 0 0 3px rgba(255,255,255,0.9), 0 0 24px 4px ${glow}, 0 0 44px 8px ${glow}`,
        };
      case "circle_lime_electric":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: `0 0 0 3px #D6FF00, 0 0 24px 4px #D6FF00CC, 0 0 48px 12px #D6FF0088, 0 0 84px 20px #D6FF0044`,
        };
      case "circle_purple_neon":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: `0 0 0 3px #B14CFF, 0 0 24px 4px #B14CFFCC, 0 0 48px 12px #B14CFF88, 0 0 84px 20px #B14CFF44`,
        };
      case "circle_pink_neon":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: `0 0 0 3px #FF3AA3, 0 0 24px 4px #FF3AA3CC, 0 0 48px 12px #FF3AA388, 0 0 84px 20px #FF3AA344`,
        };
      case "circle_cyan_neon":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: `0 0 0 3px #22D3EE, 0 0 24px 4px #22D3EECC, 0 0 48px 12px #22D3EE88, 0 0 84px 20px #22D3EE44`,
        };
      case "circle_green_neon":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: `0 0 0 3px #4ADE80, 0 0 24px 4px #4ADE80CC, 0 0 48px 12px #4ADE8088, 0 0 84px 20px #4ADE8044`,
        };
      case "circle_orange":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          background: "radial-gradient(circle at 50% 50%, #FF8A2B 0%, #FF6B00 55%, #C2410C 100%)",
          boxShadow: `0 0 0 3px rgba(255,255,255,0.85), 0 0 30px 6px rgba(255,138,43,0.65)`,
        };
      case "circle_navy":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          background: "radial-gradient(circle at 50% 50%, #1B2350 0%, #0A0E2A 100%)",
          boxShadow: `0 0 0 2px #3DA5FF, 0 0 26px 4px rgba(61,165,255,0.55)`,
        };
      case "circle_rainbow":
        // Uses global .sm-mask-rainbow CSS class — only the ring rotates, photo stays still
        return { __className: "sm-mask-rainbow", boxShadow: `0 0 22px 4px rgba(214,255,0,0.35), 0 0 42px 8px rgba(177,76,255,0.25)` };
      case "circle_holographic":
        // Uses global .sm-mask-holo CSS class — slower iridescent sweep
        return { __className: "sm-mask-holo", boxShadow: `0 0 24px 6px rgba(255,255,255,0.35)` };
      case "circle_double_ring":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: `0 0 0 2px ${glow}, 0 0 0 5px rgba(0,0,0,0.55), 0 0 0 8px ${glow}, 0 0 28px 6px ${glow}`,
        };
      case "circle_pulse":
        return {
          borderRadius: "50%",
          overflow: "hidden",
          color: glow,
          animation: "sm-mask-pulse 1.6s ease-in-out infinite",
        };
      case "circle_dashed":
        // Dashed neon border achieved with SVG mask fallback: use border+outline combo
        return {
          borderRadius: "50%",
          overflow: "hidden",
          border: `3px dashed ${glow}`,
          boxShadow: `0 0 20px 3px ${glow}, inset 0 0 0 2px rgba(0,0,0,0.35)`,
        };
      default:
        return null;
    }
  })();
  if (url) {
    const src = url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${url}` : url;
    if (maskStyle) {
      // Extract the special __className hint (used by rainbow/holo presets
      // that need a rotating pseudo-element) from the inline style object.
      const { __className, ...styleOnly } = maskStyle;
      return (
        <div
          className={__className || ""}
          style={{ width: size, height: size, boxSizing: "border-box", filter: `drop-shadow(0 4px 10px ${glow})`, ...styleOnly }}
          aria-hidden
        >
          <img
            src={src}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            style={{ borderRadius: mask === "rounded" ? "22%" : "50%" }}
          />
        </div>
      );
    }
    return (
      <img
        src={src}
        alt=""
        className="object-contain"
        loading="lazy"
        decoding="async"
        style={{ width: size, height: size, filter: `drop-shadow(0 4px 10px ${glow})` }}
        aria-hidden
      />
    );
  }
  if (emoji) {
    return (
      <span aria-hidden style={{ fontSize: size, lineHeight: 1, filter: `drop-shadow(0 4px 8px ${glow}) drop-shadow(0 2px 0 rgba(0,0,0,0.3))` }}>{emoji}</span>
    );
  }
  if (fallbackNode) return fallbackNode;
  return (
    <span aria-hidden style={{ fontSize: size, lineHeight: 1, filter: `drop-shadow(0 4px 8px ${glow}) drop-shadow(0 2px 0 rgba(0,0,0,0.3))` }}>{defaultEmoji || "✨"}</span>
  );
}

/**
 * SeasonalOverlay — decorative absolutely-positioned particle layer for
 * seasonal effects (snow / halloween / valentines / …). Rendered as spans
 * with unicode emoji + CSS animations defined in index.css. Layer is
 * pointer-events:none so it never blocks the underlying cards.
 */
function SeasonalOverlay({ effect, intensity = 60 }) {
  if (!effect || effect === "none") return null;
  const meta = {
    snow:       { emojis: ["❄", "❅", "❆"],        anim: "sm-particle-fall" },
    halloween:  { emojis: ["🎃", "👻", "🦇", "🕷️"], anim: "sm-particle-float" },
    valentines: { emojis: ["💖", "💘", "💕", "❤️"], anim: "sm-particle-float" },
    party:      { emojis: ["🎉", "🎊", "🎈", "🥳"], anim: "sm-particle-fall" },
    fireworks:  { emojis: ["✨", "💥", "⭐", "🌟"], anim: "sm-particle-burst" },
    autumn:     { emojis: ["🍂", "🍁", "🌰"],       anim: "sm-particle-fall" },
    spring:     { emojis: ["🌸", "🌷", "🌼", "🐝"], anim: "sm-particle-float" },
    summer:     { emojis: ["☀️", "🌊", "🍉", "🏖️"], anim: "sm-particle-float" },
  };
  const m = meta[effect]; if (!m) return null;
  const count = Math.round(6 + (intensity / 100) * 20); // 6-26 particles
  const particles = Array.from({ length: count }, (_, i) => {
    const emoji = m.emojis[i % m.emojis.length];
    const left = Math.random() * 100;
    const delay = -Math.random() * 8;
    const dur = 6 + Math.random() * 8;
    const size = 14 + Math.random() * 22;
    return { emoji, left, delay, dur, size, key: i };
  });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10" aria-hidden data-testid={`seasonal-${effect}`}>
      {particles.map(p => (
        <span
          key={p.key}
          className="absolute"
          style={{
            left: `${p.left}%`,
            top: "-10%",
            fontSize: p.size,
            opacity: 0.85,
            animation: `${m.anim} ${p.dur}s linear ${p.delay}s infinite`,
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))",
          }}
        >{p.emoji}</span>
      ))}
    </div>
  );
}

/**
 * PromoVideo — video player used by the "Modo Vídeo" of the secondary block.
 * Handles YouTube URLs via iframe embed and direct .mp4/.webm URLs via <video>.
 */
// External link button (YouTube or other) inside the "APRENDER A JOGAR" block.
// Enabled per language; label + URL per language; opens in a new tab.
function LearnLinkButton({ sec, lang }) {
  if (!sec[`link_button_enabled_${lang}`]) return null;
  const url = (sec[`link_button_url_${lang}`] || sec.link_button_url_pt || "").trim();
  const label = sec[`link_button_label_${lang}`] || sec.link_button_label_pt || "";
  if (!url || !label) return null;
  const icon = sec.link_button_icon || "youtube";
  const Icon = icon === "youtube" ? Youtube : icon === "play" ? Play : icon === "link" ? ExternalLink : null;
  const bg = sec.link_button_bg || "#FF0000";
  return (
    <div className="relative z-20 flex justify-center" style={{ marginTop: sec.link_button_margin_top ?? 12 }}>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 font-[Fredoka] font-black uppercase tracking-wide transition-transform hover:scale-105"
        style={{
          background: bg,
          color: sec.link_button_color || "#FFFFFF",
          fontSize: sec.link_button_size ?? 15,
          padding: "10px 22px",
          borderRadius: 9999,
          boxShadow: `0 0 18px ${bg}99, 0 4px 0 rgba(0,0,0,0.35)`,
        }}
        data-testid="learn-link-button"
      >
        {Icon && <Icon className="w-5 h-5" style={{ strokeWidth: 2.5 }} />}
        {label}
      </a>
    </div>
  );
}

// "Loja online" button for the hero side video — per language.
// variant "desktop": absolutely positioned under the video (follows its geometry).
// variant "mobile": static, rendered under the title (lg:hidden).
function SideVideoStoreButton({ h, lang, variant = "desktop", rect = null }) {
  if (!h[`side_video_store_enabled_${lang}`]) return null;
  const url = (h[`side_video_store_url_${lang}`] || h.side_video_store_url_pt || "").trim();
  const label = h[`side_video_store_label_${lang}`] || h.side_video_store_label_pt || "";
  if (!url || !label) return null;
  const icon = h.side_video_store_icon || "cart";
  const Icon = icon === "cart" ? ShoppingCart : icon === "link" ? ExternalLink : null;
  const bg = h.side_video_store_bg || "#FF2D95";
  const align = h.side_video_store_align || "center";
  const anim = h.side_video_store_anim || "pulse";
  const animCls = anim === "pulse" ? "sm-store-pulse" : anim === "shake" ? "sm-store-shake" : "";
  const anchor = (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`pointer-events-auto inline-flex items-center gap-2 font-[Fredoka] font-black uppercase tracking-wide transition-transform hover:scale-105 ${animCls}`}
      style={{
        background: bg,
        color: h.side_video_store_color || "#FFFFFF",
        fontSize: h.side_video_store_size ?? 15,
        padding: "10px 22px",
        borderRadius: 9999,
        boxShadow: `0 0 18px ${bg}99, 0 4px 0 rgba(0,0,0,0.35)`,
        "--sm-store-glow": bg,
      }}
      data-testid={variant === "mobile" ? "side-video-store-button-mobile" : "side-video-store-button"}
    >
      {Icon && <Icon className="w-5 h-5" style={{ strokeWidth: 2.5 }} />}
      {label}
    </a>
  );
  if (variant === "mobile") {
    if (h.side_video_store_mobile === false) return null;
    return (
      <div className="lg:hidden flex" style={{ width: "min(100%, calc(100vw - 48px))", justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center", marginTop: h.side_video_store_mobile_margin_top ?? 14, marginBottom: 6 }}>
        {anchor}
      </div>
    );
  }
  if (!rect) return null;
  return (
    <div
      className="absolute hidden lg:flex pointer-events-none"
      style={{
        left: rect.left,
        // Vertical anchor as % of the video height (100% = bottom edge) + px fine-tune;
        // the button is centred on that line so it overlays the video.
        top: rect.top + (rect.height * Math.max(0, Math.min(130, Number(h.side_video_store_pos_y_pct ?? 85))) / 100) + (h.side_video_store_offset_y ?? 0),
        width: rect.width,
        zIndex: 60,
        justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center",
        transform: `translate(${h.side_video_store_offset_x || 0}px, -50%)`,
      }}
      data-testid="side-video-store-overlay"
    >
      {anchor}
    </div>
  );
}

function PromoVideo({ url, poster, autoplay, muted, loop, controls, aspect, title }) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  const paddingTop = ({ "16/9": "56.25%", "4/3": "75%", "21/9": "42.85%", "1/1": "100%" })[aspect || "16/9"] || "56.25%";
  return (
    <div className="relative w-full" data-testid="promo-video">
      {title && (
        <div className="text-center font-[Fredoka] font-black text-white text-xl mb-2" style={{ textShadow: "0 0 12px rgba(61,165,255,0.6)" }}>{title}</div>
      )}
      <div className="relative w-full overflow-hidden rounded-xl" style={{ paddingTop, background: "#000" }}>
        {yt ? (
          <iframe
            title="Promo"
            src={`https://www.youtube.com/embed/${yt[1]}?autoplay=${autoplay ? 1 : 0}&mute=${muted ? 1 : 0}&loop=${loop ? 1 : 0}&playlist=${yt[1]}&controls=${controls ? 1 : 0}&modestbranding=1&rel=0`}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video
            src={url}
            poster={poster || undefined}
            autoPlay={!!autoplay}
            muted={!!muted}
            loop={!!loop}
            controls={!!controls}
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </div>
    </div>
  );
}



export default function Home() {
  const { lang } = useI18n();
  // Initialise cfg SYNCHRONOUSLY from the localStorage cache so the homepage
  // paints in the SAME frame as the intro splash (no gray "A carregar…"
  // placeholder flash on F5). If no cache exists (first visit), fall back
  // to an empty object so the homepage still renders immediately (all
  // `cfg.foo || default` accesses use their defaults) — the intro splash
  // then paints together with the dark-navy themed skeleton instead of a
  // gray "loading" screen. The real cfg fetches in the background and
  // fills in images/text seamlessly when ready.
  const [cfg, setCfg] = useState(() => readHomepageConfigCacheSync() || {});
  // GRANDE FINAL (user request Feb-2026): config lives in a separate
  // singleton so admins can enable/rename without touching the main cfg
  // JSON. When `enabled=false` the big horizontal button is simply not
  // rendered — the rest of the home layout is untouched.
  const [gfConfig, setGfConfig] = useState({ enabled: false, name: "", subtitle: "", glow_enabled: true, pulse_enabled: true, offset_y: 0 });
  // Detect the admin's floating preview iframe (opened with ?preview=1)
  // so we can skip the initial API fetch — otherwise the DB value would
  // overwrite the live-preview cfg pushed via postMessage.
  const isPreviewMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";

  useEffect(() => {
    if (isPreviewMode) return; // wait for postMessage instead
    fetchHomepageConfig().then((c) => setCfg(c || {})).catch(() => {});
  }, [isPreviewMode]);

  // GRANDE FINAL: fetch the current language-aware config. We re-fetch
  // whenever `lang` changes (user flips the switcher) so the button label
  // flips too, without a full reload.
  useEffect(() => {
    const url = `/grande-final/config${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`;
    api.get(url)
      .then((r) => setGfConfig({
        enabled: !!r?.data?.enabled,
        name: r?.data?.name || "",
        subtitle: r?.data?.subtitle || "",
        glow_enabled: r?.data?.glow_enabled !== false,
        pulse_enabled: r?.data?.pulse_enabled !== false,
        offset_y: Number.isFinite(r?.data?.offset_y) ? r.data.offset_y : 0,
      }))
      .catch(() => setGfConfig({ enabled: false, name: "", subtitle: "", glow_enabled: true, pulse_enabled: true, offset_y: 0 }));
  }, [lang]);

  // Live-preview listener: when running inside the admin's floating iframe,
  // apply cfg pushed via postMessage without needing to save to the DB.
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type === "PREVIEW_CFG" && e.data.cfg) {
        setCfg(e.data.cfg);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const t = cfg.site_theme || {};
  const bgStart = t.bg_start || "#0A0E2A";
  const bgEnd   = t.bg_end   || "#1B1352";
  const spot    = t.spotlight_color || "#4ECDC4";
  const spotI   = (t.spotlight_intensity ?? 30) / 100;
  const bgImg   = t.bg_image_url || "";
  const bgImgUrl = bgImg.startsWith("/")
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${bgImg}`
    : bgImg;
  const bgImgOpacity = (t.bg_image_opacity ?? 100) / 100;
  const bgImgSize = t.bg_image_size || "cover";
  // Fine-grained X/Y offsets override the coarse `bg_image_position` string when set.
  const hasFine = t.bg_image_pos_x != null || t.bg_image_pos_y != null;
  const bgImgPos = hasFine
    ? `${t.bg_image_pos_x ?? 50}% ${t.bg_image_pos_y ?? 0}%`
    : (t.bg_image_position || "center top");
  const bgImgRepeat = t.bg_image_repeat || "no-repeat";

  return (
    <div
      className="relative font-[Nunito] text-white overflow-x-hidden"
      style={{
        background: `linear-gradient(180deg, ${bgStart} 0%, ${bgEnd} 100%)`,
        color: t.fg_text || "#FFFFFF",
      }}
      data-testid="home-dark-root"
    >
      {/* Optional FULL-PAGE background image — sits above the gradient
       *  but below the spotlight/decor/content. Editable in admin. */}
      {bgImgUrl && (
        <div
          className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
          aria-hidden
          data-testid="home-bg-image"
        >
          <img
            src={bgImgUrl}
            alt=""
            className="absolute inset-0 w-full h-full"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            style={{
              // object-fit: cover keeps the image covering the whole container
              // regardless of transform. Transform then shifts/zooms it freely.
              objectFit: bgImgSize === "contain" ? "contain" : "cover",
              objectPosition: bgImgPos,
              opacity: bgImgOpacity,
              transform: `translate(${t.bg_image_translate_x ?? 0}px, ${t.bg_image_translate_y ?? 0}px) scale(${(t.bg_image_zoom ?? 100) / 100})`,
              transformOrigin: "center center",
              transition: "transform 120ms ease-out",
            }}
          />
        </div>
      )}
      {/* Stage-light spotlight — soft centered cone */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${spot}${Math.round(spotI*255).toString(16).padStart(2,"0")} 0%, transparent 60%)`,
        }}
        aria-hidden
      />
      {/* Decorative neon bolts + confetti overlay — purely visual */}
      <HeroDecor theme={t} />

      <div className="relative z-10">
        <HomeHeader cfg={cfg} lang={lang} />
        <Hero cfg={cfg} lang={lang} />
        <PhasesAndOnline cfg={cfg} lang={lang} gfConfig={gfConfig} />
        <StatsFooter cfg={cfg} lang={lang} />
      </div>

      <ScrollProgressBar
        color={t.progress_bar_color || t.accent || "#D6FF00"}
        glow={t.progress_bar_glow || t.accent || "#D6FF00"}
        height={t.progress_bar_height ?? 3}
      />

      <ScrollDownHint cfg={cfg} />

      {/* ============================================================
       *  MOBILE-ONLY STYLE OVERRIDES (Android + iOS)
       *  Scoped strictly to [data-testid="home-dark-root"] so it never
       *  affects other pages of the site. Neutralises the pixel-perfect
       *  desktop transforms/widths when the viewport is narrow so the
       *  layout collapses gracefully into a single column.
       * ============================================================ */}
      <style>{`
        /* NOTE: max-width:100vw on home-dark-root was removed — it conflicted
           with the mobile "scale desktop to fit" strategy where the entire
           #root is 1440px wide and transform-scaled by public/index.html. */

        /* ─────────────── CTA button effects ─────────────── */
        @keyframes cta-fx-glow-kf {
          0%,100% { box-shadow: 0 0 14px var(--cta-glow, #fff)66, 0 4px 10px -2px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -2px 4px rgba(0,0,0,0.25); }
          50%     { box-shadow: 0 0 28px var(--cta-glow, #fff)cc, 0 0 44px var(--cta-glow, #fff)66, 0 4px 10px -2px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -2px 4px rgba(0,0,0,0.25); }
        }
        .cta-fx-glow { animation: cta-fx-glow-kf 2.2s ease-in-out infinite; }

        @keyframes cta-fx-pulse-kf {
          0%,100% { transform: scale(1); }
          50%     { transform: scale(1.035); }
        }
        .cta-fx-pulse { animation: cta-fx-pulse-kf 1.6s ease-in-out infinite; }
        .cta-fx-pulse:hover { animation-play-state: paused; }

        @keyframes cta-fx-shimmer-kf {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .cta-fx-shimmer::after {
          content: "";
          position: absolute; inset: 0;
          background: linear-gradient(110deg, transparent 40%, rgba(255,255,255,0.55) 50%, transparent 60%);
          background-size: 200% 100%;
          animation: cta-fx-shimmer-kf 2.4s linear infinite;
          pointer-events: none;
          mix-blend-mode: overlay;
        }

        @keyframes cta-fx-bounce-kf {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-4px); }
        }
        .cta-fx-bounce { animation: cta-fx-bounce-kf 1.4s ease-in-out infinite; }

        @keyframes cta-fx-wobble-kf {
          0%,100% { transform: rotate(0deg); }
          25%     { transform: rotate(-1.5deg); }
          75%     { transform: rotate(1.5deg); }
        }
        .cta-fx-wobble { animation: cta-fx-wobble-kf 2.8s ease-in-out infinite; }

        @keyframes cta-fx-opacity-kf {
          0%,100% { opacity: 1; }
          50%     { opacity: 0.5; }
        }
        .cta-fx-opacity { animation: cta-fx-opacity-kf 2s ease-in-out infinite; }
        .cta-fx-opacity:hover { animation-play-state: paused; opacity: 1; }
        /* ────────────── /CTA button effects ────────────── */

        /* ─── Phase-icon Circle-Mask animations (rainbow, holographic, pulse) ─── */
        @keyframes sm-mask-spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes sm-mask-pulse {
          0%,100% { box-shadow: 0 0 0 3px currentColor, 0 0 18px 4px currentColor; }
          50%     { box-shadow: 0 0 0 5px currentColor, 0 0 34px 10px currentColor; }
        }
        /* Rotating outer ring for RAINBOW & HOLOGRAPHIC masks. Uses ::before
           so only the coloured ring rotates — the inner photo stays still. */
        .sm-mask-rainbow, .sm-mask-holo {
          position: relative;
          border-radius: 50%;
          overflow: hidden;
        }
        .sm-mask-rainbow::before, .sm-mask-holo::before {
          content: "";
          position: absolute;
          inset: -2px;
          border-radius: 50%;
          z-index: 0;
          animation: sm-mask-spin 4s linear infinite;
        }
        .sm-mask-rainbow::before {
          background: conic-gradient(from 0deg, #FF3AA3, #FF8A2B, #D6FF00, #4ADE80, #22D3EE, #3DA5FF, #B14CFF, #FF3AA3);
        }
        .sm-mask-holo::before {
          background: conic-gradient(from 45deg, #a1c4fd, #c2e9fb, #fbc2eb, #a6c1ee, #fdcbf1, #e6dee9, #a1c4fd);
          animation-duration: 8s;
        }
        .sm-mask-rainbow > img, .sm-mask-holo > img {
          position: relative;
          z-index: 1;
          border-radius: 50%;
          width: calc(100% - 8px);
          height: calc(100% - 8px);
          margin: 4px;
        }
        /* Mobile media query DISABLED — mobile devices now render the desktop
         * layout natively (scaled via viewport meta = 1440). Kept as a
         * historical stub with an impossible width so all rules become inert
         * without deleting them (easier to re-enable specific rules later). */
        @media (max-width: 0px) {
          /* Header: fixed height on mobile so the badge below can position
             predictably. Padding shrunk so pill sits flush with scroll edge. */
          [data-testid="home-dark-root"] [data-testid="home-header"] > div {
            height: 62px !important;
            min-height: 62px !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
            padding-left: 10px !important;
            padding-right: 4px !important;
            gap: 10px;
            flex-wrap: nowrap !important;
          }

          /* Hero grid outer container: reduce padding on mobile */
          [data-testid="home-dark-root"] section > .max-w-7xl {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }

          /* Hero title AI image: never overflow, neutralise transform, and
             shift 15px UP on mobile per user reference. */
          [data-testid="home-dark-root"] [data-testid="hero-title-image"] {
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            margin-left: 0 !important;
            transform: translateY(-15px) !important;
          }

          /* Hero mascot: cap width, neutralise transform */
          [data-testid="home-dark-root"] [data-testid="hero-mascot"] {
            display: none !important;
          }

          /* Hero side image (girl w/ tablet): HIDDEN on mobile per user
             request — the image took a large portion of the viewport above
             the fold and forced the phases-block to overlap it with a big
             negative margin. On mobile we now skip the image entirely. */
          [data-testid="home-dark-root"] [data-testid="hero-side"] {
            display: none !important;
          }
          [data-testid="home-dark-root"] [data-testid="hero-side-image"] {
            display: none !important;
          }

          /* CTA grid: 2-column layout on mobile (2 buttons per row, wraps
             gracefully to available space). The two internal CtaRow wrappers
             use display:contents so their 3+3 buttons flow into a single
             2-column grid on the parent → 3 rows of 2 buttons. */
          [data-testid="home-dark-root"] [data-testid="cta-grid"] {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 10px !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          [data-testid="home-dark-root"] [data-testid="cta-grid"] > div {
            display: contents !important;
          }
          [data-testid="home-dark-root"] [data-testid="hero-description"] {
            width: 100% !important;
            max-width: 100% !important;
          }

          /* Champion badge: legacy mobile override removed — badge now uses
             CSS var --cb-size which scales font-size + gaps proportionally,
             so a single JSX rule handles all viewports. Mobile-specific
             REPOSITION so the badge overlaps the top-right of the hero title
             (matches the reference anexo — badge covers upper-right portion
             of the word "APRENDER"). */
          [data-testid="home-dark-root"] [data-testid="champion-badge"] {
            left: auto !important;
            right: 4px !important;
            /* Header is fixed at 62px on mobile; badge is rotated + has star
               points that extend beyond its DOM bounding box, so we position
               its DOM top well BELOW the section start and rely on the
               rotated AABB to protrude exactly 4px above the header line. */
            top: 17px !important;
            --cb-size: 117px !important;
            width: var(--cb-size) !important;
            height: var(--cb-size) !important;
            /* Preserve the SAME rotation as desktop (from cfg via --cb-rotation)
               but drop the desktop translate(-50%,-50%) because we anchor by
               top-right pixel coords, not by % centre. */
            transform: rotate(var(--cb-rotation, -10deg)) !important;
            transform-origin: top right !important;
            /* Very high z-index so it wins over the hero content stacking
               context (title_image_z may have been raised by the admin). */
            z-index: 999 !important;
          }

          /* Header brand text — visible AND compact on mobile so the header
             fits in one row with the logo + PT/EN/ES pill next to it. */
          [data-testid="home-dark-root"] [data-testid="home-brand-text"] {
            gap: 0 !important;
          }
          [data-testid="home-dark-root"] [data-testid="home-brand-text"] > * > span,
          [data-testid="home-dark-root"] [data-testid="home-brand-text"] span[data-testid="home-logo-name"] {
            font-size: 15px !important;
            line-height: 1 !important;
          }
          [data-testid="home-dark-root"] [data-testid="home-brand-text"] span[data-testid="home-logo-tagline"] {
            font-size: 8px !important;
            letter-spacing: 0.22em !important;
            margin-top: 2px !important;
          }

          /* Header: reduce horizontal padding on mobile so the language
             pill can sit flush with the right edge of the viewport. */
          [data-testid="home-dark-root"] [data-testid="home-header"] > div {
            padding-left: 10px !important;
            padding-right: 4px !important;
          }

          /* Language pill + SAIR cluster — push to the extreme right edge on
             mobile (flush with the scroll bar per user reference). */
          [data-testid="home-dark-root"] [data-testid="header-right-cluster"] {
            margin-left: auto !important;
            margin-right: 0 !important;
          }
          [data-testid="home-dark-root"] [data-testid="lang-pill"] {
            padding: 2px !important;
            gap: 0 !important;
            margin-right: 0 !important;
          }
          [data-testid="home-dark-root"] [data-testid="lang-pill"] button {
            width: 26px !important;
            height: 26px !important;
            font-size: 10px !important;
          }

          /* Phases + Online: single column stack, neutralise offsets */
          [data-testid="home-dark-root"] [data-testid="phases-online-section"] > .max-w-7xl {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          [data-testid="home-dark-root"] [data-testid="phases-block"],
          [data-testid="home-dark-root"] [data-testid="online-block"] {
            transform: none !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          /* Online block: gap from previous phases-block and overlap the hero
             side image on mobile is not applicable (phases-block sits first).
             We DO want a comfortable breathing gap between the two boxes. */
          [data-testid="home-dark-root"] [data-testid="online-block"] {
            margin-top: 40px !important;
            z-index: 60 !important;
          }
          /* Online cards on mobile: stack the three modes VERTICALLY (single
             column) but compact each card into a row with icon+text+button
             side-by-side so all three (VS TEMPO / VS ROBOT / VS HUMANO) are
             visible inside the "JOGA ONLINE" box at the same time. */
          [data-testid="home-dark-root"] [data-testid="online-block"] .grid.grid-cols-3 {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] {
            padding: 8px 12px !important;
            min-height: 0 !important;
            flex-direction: row !important;
            align-items: center !important;
            text-align: left !important;
            gap: 12px !important;
          }
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] img,
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] > svg {
            width: 32px !important;
            height: 32px !important;
            margin-bottom: 0 !important;
            flex-shrink: 0 !important;
          }
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] h3 {
            font-size: 14px !important;
            margin-bottom: 2px !important;
            letter-spacing: 0 !important;
          }
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] p {
            font-size: 10px !important;
            line-height: 1.2 !important;
            margin-bottom: 0 !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 3 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            max-width: none !important;
          }
          /* Middle text column grows to fill available space */
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] h3,
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] p {
            flex: 1 1 auto !important;
          }
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] a,
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid^="online-card-"] button {
            font-size: 11px !important;
            padding: 6px 14px !important;
            flex-shrink: 0 !important;
          }
          /* On mobile the phases-block comes FIRST (order:1) right after the
             hero, so it must overlap the girl image the same way "online-block"
             does on desktop. Without this the girl bottom leaves a big empty
             gap above the "AS FASES DA COMPETICAO" banner. See line 664 for
             the actual margin-top override — this comment is kept for context. */
          /* Give the inner container extra top padding so the "JOGA ONLINE"
             banner sits INSIDE the box centered horizontally with proper
             breathing room above the first card (VS TEMPO). */
          [data-testid="home-dark-root"] [data-testid="online-block"] > div:nth-child(2) {
            padding-top: 78px !important;
            padding-bottom: 20px !important;
          }
          /* Ensure the inner cards column can grow freely (no clipping of the
             third card, VS HUMANO, whose description used to wrap several lines
             and push the last card below the box overflow-hidden mask). */
          [data-testid="home-dark-root"] [data-testid="online-block"] > div:nth-child(2) {
            overflow: visible !important;
          }
          /* "JOGA ONLINE" painted banner: on mobile, position INSIDE the
             block at the top (centered horizontally, tucked inside the
             padding) instead of straddling the top border. The
             PaintedTitleBanner is an inline-block whose intrinsic width is
             smaller than this wrapper — text-align:center centers it. */
          [data-testid="home-dark-root"] [data-testid="online-block"] > div:first-child {
            top: 14px !important;
            text-align: center !important;
          }
          /* Hide the phases block entirely on mobile per user reference. */
          [data-testid="home-dark-root"] [data-testid="phases-block"] {
            display: block !important;
            order: 2 !important;
            margin-top: 12px !important;
            z-index: 55 !important;
          }
          /* Order on mobile: 1) Phases do Concurso  2) Joga Online (VS TEMPO...)
             3) Vídeo "APRENDER A JOGAR". Per user reference. */
          [data-testid="home-dark-root"] [data-testid="phases-block"] {
            order: 1 !important;
          }
          [data-testid="home-dark-root"] [data-testid="online-block"] {
            order: 2 !important;
          }
          [data-testid="home-dark-root"] [data-testid="online-secondary-wrapper-mobile"] {
            order: 3 !important;
          }
          /* Give the phases inner container extra top padding so the
             "AS FASES DO CONCURSO" banner sits INSIDE the box centered
             horizontally with breathing room above the phase numbers.
             Uses the same pattern as JOGA ONLINE. */
          [data-testid="home-dark-root"] [data-testid="phases-block"] > div:last-child {
            padding-top: 78px !important;
          }
          /* "AS FASES DO CONCURSO" painted banner: on mobile, tuck INSIDE the
             box centered. Force the PaintedTitleBanner inline-block to become
             display:block + mx:auto so it centers EXACTLY (removes residual
             offset from transform/padding). */
          [data-testid="home-dark-root"] [data-testid="phases-block"] > div:first-child {
            top: 14px !important;
            text-align: center !important;
          }
          [data-testid="home-dark-root"] [data-testid="phases-block"] > div:first-child > div[data-testid="phases-painted-title"] {
            display: block !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          /* Painted banner text: shrink to fit inside the hand-painted blob on
             narrow viewports (default is text-2xl = 24px which pushes the outer
             letters of long titles like "AS FASES DA COMPETIÇÃO" outside the
             paint region). Also add extra horizontal padding to keep the first
             / last letter safely inside the blob's rounded ends. */
          [data-testid="home-dark-root"] [data-testid="phases-painted-title"] h2,
          [data-testid="home-dark-root"] [data-testid="online-block"] [data-testid="phases-painted-title"] h2,
          [data-testid="home-dark-root"] [data-testid="online-secondary-wrapper"] [data-testid="phases-painted-title"] h2,
          [data-testid="home-dark-root"] [data-testid="online-secondary-wrapper-mobile"] [data-testid="phases-painted-title"] h2 {
            font-size: 18px !important;
            letter-spacing: 0 !important;
          }
          [data-testid="home-dark-root"] [data-testid="phases-painted-title"] {
            padding-left: 46px !important;
            padding-right: 46px !important;
          }
          /* Phases inner illustration row: reduce icon size, keep single row */
          [data-testid="home-dark-root"] [data-testid="phases-block"] .grid-cols-\\[1fr_auto_1fr_auto_1fr\\] {
            gap: 6px !important;
          }

          /* CTA tiles: allow labels to wrap without clipping, shrink text */
          [data-testid="home-dark-root"] [data-testid="cta-grid"] a {
            font-size: 11px !important;
            padding: 4px 6px !important;
            gap: 6px !important;
          }
          [data-testid="home-dark-root"] [data-testid="cta-grid"] a span {
            font-size: 11px !important;
            line-height: 1.05 !important;
          }
          [data-testid="home-dark-root"] [data-testid="cta-grid"] a svg {
            width: 22px !important;
            height: 22px !important;
          }

          /* Phases block: mobile uses the dedicated vertical stack render
             (data-testid="phases-mobile-layout"). Hide the desktop 3-column
             layout, show the vertical stack. */
          [data-testid="home-dark-root"] [data-testid="phases-block"] [data-testid="phases-desktop-layout"] {
            display: none !important;
          }
          [data-testid="home-dark-root"] [data-testid="phases-block"] [data-testid="phases-mobile-layout"] {
            display: block !important;
          }
          /* Phases block: shrink inner elements */
          [data-testid="home-dark-root"] [data-testid="phases-block"] {
            min-width: 0 !important;
          }
          [data-testid="home-dark-root"] [data-testid="phases-block"] > div:last-child {
            padding-left: 16px !important;
            padding-right: 16px !important;
            min-width: 0 !important;
            overflow: hidden !important;
          }
          [data-testid="home-dark-root"] [data-testid="phases-block"] [data-testid^="phase-num-"] > div:first-child {
            width: 26px !important;
            height: 26px !important;
            font-size: 13px !important;
          }
          /* Painted banner over phases/online: never wider than viewport */
          [data-testid="home-dark-root"] [data-testid="phases-block"] > div:first-child,
          [data-testid="home-dark-root"] [data-testid="online-block"] > div:first-child {
            width: 90% !important;
            min-width: 0 !important;
            max-width: calc(100vw - 40px) !important;
          }
          [data-testid="home-dark-root"] [data-testid="phases-block"] > div:first-child svg,
          [data-testid="home-dark-root"] [data-testid="online-block"] > div:first-child svg {
            width: 100% !important;
            height: auto !important;
          }

          /* FAIXA (StatsFooter): stack items vertically + full width */
          [data-testid="home-dark-root"] [data-testid="stats-footer"] > div:first-of-type {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
            padding: 16px 14px !important;
          }
          [data-testid="home-dark-root"] [data-testid="stats-footer"] > div:first-of-type > [aria-hidden][style*="width: 1px"] {
            display: none !important;
          }
          [data-testid="home-dark-root"] [data-testid="stats-footer"] [data-testid^="stat-"] {
            justify-content: center;
          }
          [data-testid="home-dark-root"] [data-testid="stats-footer"] [data-testid="footer-cta"] {
            width: 100%;
          }
          /* Social icons — centered horizontally in the footer on mobile. */
          [data-testid="home-dark-root"] [data-testid="stats-footer"] [data-testid="footer-socials"] {
            justify-content: center !important;
            width: 100%;
          }

          /* Secondary "Aprender a Jogar" video block: readable padding */
          [data-testid="home-dark-root"] [data-testid="online-secondary-block"] {
            padding: 28px 8px 12px !important;
          }
          /* "APRENDER A JOGAR" painted banner: center the intrinsic inline-block
             PaintedTitleBanner (same fix as the JOGA ONLINE banner). */
          [data-testid="home-dark-root"] [data-testid="online-secondary-wrapper"] > div:first-child {
            text-align: center !important;
          }
        }
        @media (max-width: 0px) {
          [data-testid="home-dark-root"] [data-testid="hero-side-image"] {
            max-width: 260px !important;
          }
        }
      `}</style>
    </div>
  );
}

/** ScrollProgressBar — thin neon bar fixed at the top of the viewport
 *  that grows from 0 → 100% as the user scrolls the page. Uses a passive
 *  scroll listener throttled with requestAnimationFrame + direct DOM writes
 *  (bypasses React re-renders) so it never contributes to scroll jank. */
function ScrollProgressBar({ color = "#D6FF00", glow = "#D6FF00", height = 5 }) {
  const barRef = React.useRef(null);
  React.useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = document.documentElement;
      const max = (el.scrollHeight - el.clientHeight) || 1;
      const pct = Math.min(100, Math.max(0, (el.scrollTop / max) * 100));
      if (barRef.current) barRef.current.style.width = pct + "%";
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <div
      className="fixed top-0 left-0 pointer-events-none"
      style={{
        height,
        width: "100%",
        zIndex: 9999,
        background: "rgba(255,255,255,0.14)",
      }}
      aria-hidden
      data-testid="scroll-progress-track"
    >
      <div
        ref={barRef}
        style={{
          height: "100%",
          width: "0%",
          background: `linear-gradient(90deg, ${color} 0%, ${glow} 100%)`,
          boxShadow: `0 0 10px ${glow}FF, 0 0 20px ${glow}AA, 0 0 32px ${glow}66`,
          transition: "width 60ms linear",
        }}
        data-testid="scroll-progress-bar"
      />
    </div>
  );
}



/** Blinking "scroll down" arrow — fixed at the bottom-center of the viewport
 *  to signal that more content is below the fold. Hides itself once the user
 *  has scrolled more than the configured threshold OR reached near the bottom. */
function ScrollDownHint({ cfg }) {
  const sh = cfg?.scroll_hint || {};
  const enabled       = sh.enabled !== false;
  const bottomOffset  = sh.bottom_offset ?? 24;
  const size          = sh.size ?? 54;
  const iconColor     = sh.icon_color || "#3DA5FF";
  const bgColor       = sh.bg_color   || "#0F1A55";
  const hideAfter     = sh.hide_after_scroll ?? 120;
  const bounceSpeed   = sh.bounce_speed ?? 1.6;

  const [visible, setVisible] = React.useState(true);
  React.useEffect(() => {
    if (!enabled) { setVisible(false); return undefined; }
    let raf = 0;
    const check = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      const nearBottom = (window.innerHeight + y) >= (document.documentElement.scrollHeight - 40);
      setVisible(y < hideAfter && !nearBottom);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; check(); });
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", check);
    };
  }, [enabled, hideAfter]);
  if (!enabled) return null;
  const smoothScroll = () => {
    window.scrollTo({ top: window.innerHeight * 0.85, behavior: "smooth" });
  };
  // rgba helper for the pulse rings from the neon color
  const hex2rgba = (hex, a) => {
    const h = String(hex || "#3DA5FF").replace("#", "");
    const n = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  };
  return (
    <>
      <style>{`
        @keyframes sm-scroll-bounce {
          0%,100% { transform: translate(-50%, 0); opacity: 0.85; }
          50%     { transform: translate(-50%, 14px); opacity: 1; }
        }
        @keyframes sm-scroll-pulse {
          0%,100% { box-shadow: 0 0 0 0 ${hex2rgba(iconColor, 0.55)}, 0 0 20px 4px ${hex2rgba(iconColor, 0.35)}; }
          70%     { box-shadow: 0 0 0 22px ${hex2rgba(iconColor, 0)}, 0 0 30px 8px ${hex2rgba(iconColor, 0.15)}; }
        }
      `}</style>
      <button
        type="button"
        onClick={smoothScroll}
        aria-label="Continuar a ver — scroll para baixo"
        className="fixed left-1/2 z-[9998] rounded-full grid place-items-center cursor-pointer transition-opacity duration-300"
        style={{
          bottom: bottomOffset,
          width: size,
          height: size,
          transform: "translate(-50%, 0)",
          background: `linear-gradient(180deg, ${bgColor} 0%, #071B5A 100%)`,
          border: `2px solid ${iconColor}`,
          animation: visible ? `sm-scroll-bounce ${bounceSpeed}s ease-in-out infinite, sm-scroll-pulse ${bounceSpeed * 1.25}s ease-out infinite` : "none",
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? "auto" : "none",
        }}
        data-testid="scroll-down-hint"
      >
        <ChevronDown style={{ width: Math.round(size * 0.55), height: Math.round(size * 0.55), color: iconColor, strokeWidth: 3, filter: `drop-shadow(0 0 6px ${iconColor})` }} />
      </button>
    </>
  );
}

/** Decorative SVG overlay — neon lightning bolts + scattered math confetti.
 *  Repeats fixed across the hero to mimic the gaming poster vibe. */
function HeroDecor({ theme }) {
  const bolt = theme.bolt_color || "#E5FF00";
  const conf = theme.confetti_color || "#FFD93D";
  const opacity = (theme.decor_density ?? 60) / 100;
  // Twinkling multi-colour star field — many stars, random positions/sizes/
  // colours/timings, each cintilating (opacity + scale + rotate) on its own beat.
  const stars = React.useMemo(() => {
    const palette = ["#FFD93D", "#FF5DA2", "#3DA5FF", "#7CFF6B", "#C77DFF", "#FF8A3D", "#FFFFFF", "#00E5FF", "#FFE14D"];
    const rand = (min, max) => min + Math.random() * (max - min);
    return Array.from({ length: 54 }, (_, i) => {
      const color = palette[i % palette.length];
      return {
        id: i,
        top: rand(2, 94),
        left: rand(1, 97),
        size: rand(6, 20),
        color,
        dur: rand(1.6, 4.2),
        delay: rand(0, 3.5),
      };
    });
  }, []);
  const STAR_CLIP = "polygon(50% 0,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)";
  return (
    <div className="pointer-events-none absolute inset-0 z-0" aria-hidden style={{ opacity }}>
      <svg className="absolute top-10 left-[8%] w-12 h-16" viewBox="0 0 30 50" fill={bolt}><path d="M16 0L3 28h8L8 50l18-30h-9z" /></svg>
      <svg className="absolute top-1/2 right-[6%] w-14 h-20" viewBox="0 0 30 50" fill={bolt}><path d="M16 0L3 28h8L8 50l18-30h-9z" /></svg>
      <svg className="absolute bottom-32 right-[28%] w-10 h-14 opacity-80" viewBox="0 0 30 50" fill={bolt}><path d="M16 0L3 28h8L8 50l18-30h-9z" /></svg>
      {/* Scattered + and × symbols */}
      <div className="absolute top-32 left-[18%] text-2xl font-black" style={{ color: conf, textShadow: `0 0 8px ${conf}` }}>+</div>
      <div className="absolute top-44 left-[26%] text-3xl font-black" style={{ color: conf, textShadow: `0 0 8px ${conf}` }}>×</div>
      <div className="absolute top-24 left-[40%] text-xl font-black" style={{ color: bolt, textShadow: `0 0 8px ${bolt}` }}>!</div>
      {/* Twinkling multi-colour stars */}
      <div className="absolute inset-0 overflow-hidden" data-testid="hero-starfield">
        {stars.map((s) => (
          <span
            key={s.id}
            className="absolute"
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              background: s.color,
              clipPath: STAR_CLIP,
              WebkitClipPath: STAR_CLIP,
              filter: `drop-shadow(0 0 ${Math.round(s.size * 0.5)}px ${s.color})`,
              animation: `sm-star-twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
              willChange: "opacity, transform",
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* =========================================================================
   HEADER — logo + circular icon action buttons + lang pill + SAIR button
   ========================================================================= */

/**
 * HeaderLink — polymorphic wrapper that renders each header brand element
 * with a configurable internal / external / empty link:
 *   - href starts with "http" / "mailto:" / "tel:" → external <a target="_blank">
 *   - href is a valid internal path → react-router <Link>
 *   - href is empty / null → renders as a plain <span> (not clickable)
 */
function HeaderLink({ href, testid, children, className = "" }) {
  const clean = (href || "").trim();
  if (!clean) return <span className={className} data-testid={testid}>{children}</span>;
  const linkCls = `${className} hover:underline`.trim();
  const isExternal = /^(https?:|mailto:|tel:)/i.test(clean);
  if (isExternal) {
    return (
      <a href={clean} target={clean.startsWith("http") ? "_blank" : undefined} rel={clean.startsWith("http") ? "noreferrer" : undefined} className={linkCls} data-testid={testid}>
        {children}
      </a>
    );
  }
  return <Link to={clean} className={linkCls} data-testid={testid}>{children}</Link>;
}

// Dropdown submenu for the "Jogar Online" header button — used when the
// main button has NO url and there are submenu items. Click toggles, click
// outside / Escape closes. Each item is an internal Link or external <a>.
function JogarOnlineSubmenu({ label, items, activeLang, btnCls, btnStyle }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="relative" ref={wrapRef} data-testid="header-jogar-online-wrap">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={btnCls}
        style={btnStyle}
        data-testid="header-jogar-online-btn"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Gamepad2 className="w-5 h-5" style={{ strokeWidth: 2.5 }} />
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} style={{ strokeWidth: 2.5 }} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[220px] rounded-2xl overflow-hidden z-40"
          style={{
            background: "#0E1740",
            border: "1px solid rgba(214,255,0,0.35)",
            boxShadow: "0 12px 36px rgba(0,0,0,0.55), 0 0 24px rgba(214,255,0,0.15)",
          }}
          role="menu"
          data-testid="header-jogar-online-submenu"
        >
          {items.map((it, i) => {
            const itLabel = (it[`label_${activeLang}`] || it.label_pt || it.id || "").toString();
            const itUrl = (it.url || "").trim() || "/treinar";
            const isExternal = itUrl.startsWith("http") || itUrl.startsWith("mailto");
            const Tag = isExternal ? "a" : Link;
            const linkProps = isExternal ? { href: itUrl, target: "_blank", rel: "noreferrer" } : { to: itUrl };
            return (
              <Tag
                key={it.id || i}
                {...linkProps}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-4 py-3 font-[Fredoka] font-black uppercase text-sm tracking-wide text-white hover:bg-white/10 transition-colors"
                style={{ borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`header-jogar-online-submenu-item-${it.id || i}`}
                role="menuitem"
              >
                <Gamepad2 className="w-4 h-4" style={{ color: "#D6FF00", strokeWidth: 2.5 }} />
                <span>{itLabel}</span>
              </Tag>
            );
          })}
        </div>
      )}
    </div>
  );
}

// HomeHeader is memoized: it re-renders only when `cfg` or `lang` change.
// This prevents useless re-renders when parent state (e.g. floating preview
// zoom, admin controls) updates but neither prop actually changed.
export const HomeHeader = React.memo(function HomeHeader({ cfg, lang }) {
  const { lang: activeLang, setLang } = useI18n();
  const [langOpen, setLangOpen] = useState(false);
  const LANGS = [
    { code: "pt", label: "PT" }, { code: "en", label: "EN" }, { code: "es", label: "ES" },
  ];
  const jogarBtn = cfg.jogar_online_button || {};
  const actions = cfg.header_actions || {};
  const buttons = Array.isArray(actions.buttons) ? actions.buttons : [];

  return (
    <header className="sticky top-0 z-[70]" style={{ background: "#04081F", borderBottom: "1px solid rgba(255,255,255,0.06)" }} data-testid="home-header">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center gap-6">
        <div className="flex-shrink-0 flex items-center gap-3 group" data-testid="home-logo">
          {/* Logo image — wrapped in its own configurable link */}
          <HeaderLink href={cfg.header_links?.logo_url ?? "/"} testid="home-logo-link">
            {(() => {
              const r = cfg.logo_ring || {};
              const enabled = r.enabled !== false;
              const size = r.logo_size || 56;
              const thick = r.thickness ?? 3;
              const dist  = r.distance ?? 2;
              const outer = size + 2 * (thick + dist);
            let ringBg = r.color_primary || "#000";
            if (r.style === "rainbow") {
              const c1 = r.color_primary   || "#000000";
              const c2 = r.color_glow      || "#FF00C8";
              const c3 = r.color_secondary || "#FFD93D";
              ringBg = `conic-gradient(from 0deg,${c1},${c2},${c3},${c2},${c1})`;
            } else if (r.style === "gradient") {
              ringBg = `linear-gradient(135deg,${r.color_primary||"#000"},${r.color_secondary||"#FFD93D"})`;
            }
            const ringStyle = { width: outer, height: outer, padding: thick, background: ringBg, borderRadius: "9999px" };
            const dir = r.direction || "normal";
            if ((r.intensity || 0) > 0 || r.animation === "glow") {
              const i = r.intensity || 25;
              ringStyle.boxShadow = `0 0 ${i/2}px ${i/4}px ${r.color_glow||"#FF00C8"}`;
            }
            if (r.animation === "rotate") ringStyle.animation = `brand-spin ${r.speed||6}s linear infinite ${dir}`;
            if (r.animation === "pulse")  ringStyle.animation = `brand-pulse ${r.speed||6}s ease-in-out infinite`;
            if (r.style === "dashed")     { ringStyle.background = "transparent"; ringStyle.border = `${thick}px dashed ${r.color_primary||"#000"}`; ringStyle.padding = 0; }
            // No visible fallback letter when the logo hasn't arrived yet.
            // On the very first paint (no cache) or during hot reload the
            // config takes ~200ms to resolve — rendering a yellow "E"
            // placeholder in that gap made the logo look broken. Render
            // an empty transparent circle instead so the ring/gap keeps
            // its dimensions and the real logo slides in without any
            // perceived flash.
            const inner = cfg.logo_url
              ? <img src={cfg.logo_url} alt="logo" className="w-full h-full object-cover rounded-full bg-white" loading="eager" decoding="async" fetchPriority="high" data-testid="home-logo-image" />
              : <div className="w-full h-full rounded-full bg-white" data-testid="home-logo-placeholder" aria-hidden="true" />;
            return enabled ? (
              <div style={ringStyle} className="group-hover:scale-105 transition-transform">
                <div style={{ padding: dist, background: "white", borderRadius: "9999px", width: "100%", height: "100%" }}>{inner}</div>
              </div>
            ) : (
              <div style={{ width: size, height: size }} className="rounded-full overflow-hidden bg-white shadow-sm group-hover:scale-105 transition-transform">{inner}</div>
            );
            })()}
          </HeaderLink>
          {(cfg.logo_name || cfg.logo_tagline) && (
            <div className="flex flex-col leading-tight" data-testid="home-brand-text">
              <HeaderLink href={cfg.header_links?.brand_name_url} testid="home-logo-name-link">
                <span
                  className="font-[Fredoka]"
                  style={{
                    color: cfg.logo_name_style?.color || "#FFFFFF",
                    fontSize: `${cfg.logo_name_style?.size ?? 24}px`,
                    fontWeight: cfg.logo_name_style?.weight ?? 900,
                    letterSpacing: `${cfg.logo_name_style?.letter_spacing ?? -0.5}px`,
                    textTransform: cfg.logo_name_style?.uppercase ? "uppercase" : "none",
                    textShadow: (cfg.logo_name_style?.shadow ?? true)
                      ? "0 2px 0 rgba(0,0,0,0.55), 0 0 12px rgba(61,165,255,0.4)"
                      : "none",
                    lineHeight: 1.05,
                  }}
                  data-testid="home-logo-name"
                >
                  {cfg.logo_name?.[activeLang] || cfg.logo_name?.pt || ""}
                </span>
              </HeaderLink>
              {(cfg.logo_tagline?.[activeLang] || cfg.logo_tagline?.pt) && (
                <HeaderLink href={cfg.header_links?.brand_tagline_url} testid="home-logo-tagline-link">
                  <span
                    className=""
                    style={{
                      color: cfg.logo_tagline_style?.color || "#FFD93D",
                      fontSize: `${cfg.logo_tagline_style?.size ?? 10}px`,
                      fontWeight: cfg.logo_tagline_style?.weight ?? 700,
                      letterSpacing: `${cfg.logo_tagline_style?.letter_spacing ?? 2.5}px`,
                      textTransform: (cfg.logo_tagline_style?.uppercase ?? true) ? "uppercase" : "none",
                      textShadow: (cfg.logo_tagline_style?.shadow ?? true)
                        ? "0 0 8px rgba(255,217,61,0.5)"
                        : "none",
                    }}
                    data-testid="home-logo-tagline"
                  >
                    {cfg.logo_tagline?.[activeLang] || cfg.logo_tagline?.pt}
                  </span>
                </HeaderLink>
              )}
            </div>
          )}
        </div>

        {/* Center: rounded-square icon action buttons with neon glow (matches reference) */}
        <nav className="hidden lg:flex items-center gap-5 flex-1 justify-center" data-testid="home-actions">
          {buttons.map((b) => {
            const Icon = ICONS[b.icon] || Mail;
            const isActive = b.active;
            const isExternal = b.url?.startsWith("http") || b.url?.startsWith("mailto");
            const Tag = isExternal ? "a" : Link;
            const linkProps = isExternal ? { href: b.url || "#", target: "_blank", rel: "noreferrer" } : { to: b.url || "/" };
            const tip = pick(b, "tooltip", lang) || b.tooltip_pt || b.id;
            const bgColor = isActive ? (b.active_bg || "#E5FF00") : (b.bg || "#0E1740");
            const fgColor = isActive ? (b.active_fg || b.fg || "#0A0E2A") : (b.fg || "#FFFFFF");
            return (
              <Tag
                key={b.id}
                {...linkProps}
                title={tip}
                className="relative flex flex-col items-center group"
                data-testid={`header-action-${b.id}`}
              >
                <div
                  className="grid place-items-center group-hover:scale-110 transition-transform"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: bgColor,
                    color: fgColor,
                    border: isActive ? `2px solid ${bgColor}` : "1px solid rgba(255,255,255,0.08)",
                    boxShadow: isActive
                      ? `0 0 24px ${bgColor}AA, 0 4px 0 rgba(0,0,0,0.35), inset 0 -3px 0 rgba(0,0,0,0.18), inset 0 2px 0 rgba(255,255,255,0.35)`
                      : `0 4px 0 rgba(0,0,0,0.45), inset 0 -3px 0 rgba(0,0,0,0.32), inset 0 2px 0 rgba(255,255,255,0.10)`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ strokeWidth: 2.5 }} />
                </div>
                <span
                  className="text-[11px] font-[Fredoka] font-black tracking-wider uppercase mt-1.5"
                  style={{ color: isActive ? bgColor : "rgba(255,255,255,0.95)", textShadow: isActive ? `0 0 8px ${bgColor}AA` : "0 1px 0 rgba(0,0,0,0.55)" }}
                >
                  {tip}
                </span>
              </Tag>
            );
          })}
        </nav>

        {/* Right: "Jogar Online" pill + unified language capsule + SAIR rounded-square */}
        <div className="flex items-center gap-4" data-testid="header-right-cluster">
          {jogarBtn.enabled !== false && (jogarBtn[`label_${activeLang}`] || jogarBtn.label_pt) && (() => {
            const label = jogarBtn[`label_${activeLang}`] || jogarBtn.label_pt;
            const url = (jogarBtn.url || "").trim();
            const hasUrl = !!url;
            const submenu = Array.isArray(jogarBtn.submenu) ? jogarBtn.submenu : [];
            const showSubmenu = !hasUrl && submenu.length > 0;
            const btnStyle = {
              background: "#D6FF00",
              color: "#0A0E2A",
              fontSize: 14,
              padding: "10px 20px",
              borderRadius: 9999,
              boxShadow: "0 0 20px rgba(214,255,0,0.55), 0 4px 0 rgba(0,0,0,0.35), inset 0 -3px 0 rgba(0,0,0,0.18), inset 0 2px 0 rgba(255,255,255,0.45)",
            };
            const btnCls = "hidden sm:inline-flex items-center gap-2 font-[Fredoka] font-black uppercase tracking-wide hover:scale-105 transition-transform";

            // Case 1: no url + submenu → open dropdown
            if (showSubmenu) {
              return (
                <JogarOnlineSubmenu
                  label={label}
                  items={submenu}
                  activeLang={activeLang}
                  btnCls={btnCls}
                  btnStyle={btnStyle}
                />
              );
            }
            // Case 2: has url (or no submenu) → direct link (current behavior; fallback /treinar)
            const finalUrl = hasUrl ? url : "/treinar";
            const isExternal = finalUrl.startsWith("http") || finalUrl.startsWith("mailto");
            const Tag = isExternal ? "a" : Link;
            const linkProps = isExternal ? { href: finalUrl, target: "_blank", rel: "noreferrer" } : { to: finalUrl };
            return (
              <Tag
                {...linkProps}
                className={btnCls}
                style={btnStyle}
                data-testid="header-jogar-online-btn"
              >
                <Gamepad2 className="w-5 h-5" style={{ strokeWidth: 2.5 }} />
                {label}
              </Tag>
            );
          })()}
          <div
            className="flex items-center gap-1 p-1"
            style={{
              background: actions.lang_pill_bg || "#0E1740",
              borderRadius: 9999,
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "inset 0 2px 4px rgba(0,0,0,0.45)",
            }}
            data-testid="lang-pill"
          >
            {LANGS.map((l) => {
              const active = activeLang === l.code;
              return (
                <button
                  key={l.code}
                  type="button"
                  onPointerDown={(e) => {
                    // Use pointerdown for INSTANT feedback: click events are
                    // sometimes swallowed while the big i18n re-render is in
                    // flight, which made the pill feel sluggish. We prevent
                    // the default so click won't fire twice, then trigger the
                    // language change synchronously.
                    e.preventDefault();
                    if (l.code !== activeLang) setLang(l.code);
                  }}
                  onClick={(e) => {
                    // Fallback for keyboard-triggered clicks (Enter/Space): if
                    // pointerdown didn't set the lang (mouse/touch already did),
                    // this ensures keyboard users still work.
                    if (l.code !== activeLang) setLang(l.code);
                  }}
                  aria-pressed={active}
                  aria-label={`Change language to ${l.label}`}
                  className="relative rounded-full font-[Fredoka] font-black text-[12px] tracking-wide transition-colors grid place-items-center cursor-pointer select-none touch-manipulation"
                  style={{
                    minWidth: 44,
                    minHeight: 44,
                    width: 44,
                    height: 44,
                    padding: 0,
                    background: active ? (actions.lang_active_bg || "#D6FF00") : "transparent",
                    color: active ? (actions.lang_active_fg || "#0A0E2A") : (actions.lang_inactive_fg || "#FFFFFF"),
                    boxShadow: active ? `0 0 14px ${actions.lang_active_bg || "#D6FF00"}AA` : "none",
                    border: "none",
                    outline: "none",
                  }}
                  data-testid={`lang-${l.code}`}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
          <Link
            to={actions.exit_url || "/logout"}
            className="hidden md:inline-flex flex-col items-center"
            data-testid="header-exit"
          >
            <div
              className="grid place-items-center hover:scale-105 transition-transform"
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: actions.exit_bg || "#0E1740",
                color: actions.exit_icon_color || "#FF6A00",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: `0 4px 0 rgba(0,0,0,0.45), inset 0 -3px 0 rgba(0,0,0,0.32), inset 0 2px 0 rgba(255,255,255,0.10)`,
              }}
            >
              <LogOut className="w-6 h-6" style={{ strokeWidth: 2.5 }} />
            </div>
            <span className="text-[11px] font-[Fredoka] font-black tracking-wider uppercase mt-1.5" style={{ color: actions.exit_icon_color || "#FF6A00", textShadow: `0 0 8px ${actions.exit_icon_color || "#FF6A00"}66` }}>
              {actions[`exit_label_${activeLang}`] || actions.exit_label_pt || "SAIR"}
            </span>
          </Link>
        </div>
      </div>
    </header>
  );
});

/* ============================================================================
   CtaRow — renders a 3-column row of glossy CTA tiles with a fixed aspect
   ratio. Used twice in Hero: primaries (taller, portrait) + secondaries
   (shorter, landscape) to match the reference image's two-row layout.
   ============================================================================ */
const CtaRow = React.memo(function CtaRow({ items, lang, height, layout = {}, textSize, iconRotate = 0, textRotate = 0 }) {
  const gap     = layout.gap ?? 12;
  const baseRadius = layout.radius ?? 14;
  // Fixed text size for this row (px). If not provided, falls back to a
  // legacy clamp() range for backwards compatibility.
  const rawMin  = layout.text_min ?? 13;
  const rawMax  = layout.text_max ?? 16;
  const tMin    = Math.min(rawMin, rawMax);
  const tMax    = Math.max(rawMin, rawMax);
  const defaultFontSize = textSize
    ? `${textSize}px`
    : `clamp(${tMin}px, 1.25vw, ${tMax}px)`;
  const shade = (hex, factor) => {
    const h = String(hex || "#000").replace("#", "");
    const n = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    const r = Math.max(0, Math.min(255, Math.round(parseInt(n.slice(0,2),16) * factor)));
    const g = Math.max(0, Math.min(255, Math.round(parseInt(n.slice(2,4),16) * factor)));
    const b = Math.max(0, Math.min(255, Math.round(parseInt(n.slice(4,6),16) * factor)));
    return `rgb(${r},${g},${b})`;
  };
  // Filter out CTAs explicitly hidden by admin.
  const visible = items.filter(c => c.visible !== false);
  return (
    <div className="grid grid-cols-3" style={{ gap }}>
      {visible.map((c) => {
        const Icon = ICONS[c.icon] || Gamepad2;
        const bg = c.bg_color || c.bg || "#E5FF00";
        const fg = c.text_color || c.fg || "#0A0E2A";
        // Per-CTA overrides (all optional; fall back to row defaults)
        const btnH = (c.size && c.size > 0) ? c.size : height;
        const shape = c.shape || "rounded";
        const radius = shape === "square" ? 2 : shape === "pill" ? Math.round(btnH / 2) : baseRadius;
        const font = c.font || "Fredoka";
        const iconPx = Math.round(btnH * (layout.icon_pct ?? 54) / 100);
        const buttonFontSize = c.font_size ? `${c.font_size}px` : defaultFontSize;
        const effect = c.effect || "none";
        const fxClass = effect !== "none" ? `cta-fx-${effect}` : "";
        // Resolve destination href based on action_type. External CTAs can
        // carry a per-language URL (url_pt/url_en/url_es); fall back to the
        // single `url` when the active language has none.
        const isExternal = c.action_type === "external";
        const externalUrl = c[`url_${lang}`] || c.url || "";
        const internalUrl = c.target_page || c.url || "";
        // A CTA is "inactive" when it has no destination configured.
        const hasLink = isExternal ? !!externalUrl : !!internalUrl;
        const href = isExternal ? (externalUrl || "#") : (internalUrl || "/");
        const subtitle = pick(c, "subtitle", lang);
        // Wrap in a positioning wrapper so offset_x/y don't fight with hover transforms
        const wrapperStyle = {
          transform: (c.offset_x || c.offset_y)
            ? `translate(${c.offset_x || 0}px, ${c.offset_y || 0}px)`
            : undefined,
        };
        const commonProps = {
          className: `group relative overflow-hidden flex items-center transition-transform ${hasLink ? "hover:-translate-y-0.5 hover:scale-[1.02] cursor-pointer" : "cursor-not-allowed"} ${fxClass}`,
          style: {
            height: btnH,
            borderRadius: radius,
            padding: "4px 10px",
            gap: 8,
            background: `linear-gradient(180deg, ${shade(bg, 1.15)} 0%, ${bg} 50%, ${shade(bg, 0.78)} 100%)`,
            border: `2px solid ${shade(bg, 1.25)}`,
            boxShadow: `
              0 0 14px ${shade(bg, 0.95)}66,
              0 4px 10px -2px rgba(0,0,0,0.55),
              inset 0 1px 0 rgba(255,255,255,0.55),
              inset 0 -2px 4px rgba(0,0,0,0.25)
            `,
            "--cta-glow": shade(bg, 1.1),
            // Inactive (no link) CTAs are dimmed so it's obvious they're off.
            opacity: hasLink ? 1 : 0.45,
            // Isolate paint of each CTA (safe: has overflow-hidden). Reduces
            // the area the compositor must repaint when neighbors change or
            // when a hover effect animates — significant scroll perf win.
            contain: "layout paint",
          },
          "data-testid": `cta-${c.id}`,
        };
        const inner = (
          <>
            {/* Top 20% glossy highlight band */}
            <div
              className="absolute inset-x-0 top-0 pointer-events-none"
              style={{
                height: "20%",
                background: "linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 100%)",
                borderRadius: `${Math.max(0, radius - 2)}px ${Math.max(0, radius - 2)}px 0 0`,
              }}
              aria-hidden
            />
            {/* Inactive (no link): diagonal "no-entry" stroke across the button */}
            {!hasLink && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  borderRadius: radius,
                  background:
                    "linear-gradient(to top right, transparent calc(50% - 2px), rgba(185,16,16,0.92) calc(50% - 2px), rgba(185,16,16,0.92) calc(50% + 2px), transparent calc(50% + 2px))",
                }}
                aria-hidden
              />
            )}
            {/* Icon — left-aligned, vertically centered, rotatable */}
            <Icon
              className="flex-shrink-0 relative"
              style={{
                color: fg,
                width: iconPx,
                height: iconPx,
                strokeWidth: 2.4,
                filter: "drop-shadow(0 1px 0 rgba(0,0,0,0.18))",
                transform: `rotate(${iconRotate}deg)`,
                transformOrigin: "center",
              }}
              aria-hidden
            />
            {/* Label + optional subtitle */}
            <div
              className="relative flex flex-col justify-center min-w-0"
              style={{
                transform: `rotate(${textRotate}deg)`,
                transformOrigin: "left center",
              }}
            >
              <span
                className="font-black uppercase leading-tight"
                style={{
                  color: fg,
                  fontFamily: `"${font}", ui-sans-serif, system-ui, sans-serif`,
                  fontSize: buttonFontSize,
                  lineHeight: 1.05,
                  letterSpacing: "0.01em",
                  textShadow: fg === "#FFFFFF" ? "0 1px 0 rgba(0,0,0,0.22)" : "none",
                  wordBreak: "normal",
                  overflowWrap: "normal",
                  whiteSpace: "normal",
                  display: "inline-block",
                }}
              >
                {pick(c, "label", lang)}
              </span>
              {subtitle && (
                <span
                  className="font-semibold uppercase leading-tight opacity-90"
                  style={{
                    color: fg,
                    fontFamily: `"${font}", ui-sans-serif, system-ui, sans-serif`,
                    fontSize: `calc(${buttonFontSize} * 0.65)`,
                    lineHeight: 1.0,
                    letterSpacing: "0.02em",
                    marginTop: 1,
                  }}
                >
                  {subtitle}
                </span>
              )}
            </div>
          </>
        );
        return (
          <div key={c.id} style={wrapperStyle}>
            {!hasLink ? (
              <div {...commonProps} aria-disabled="true">{inner}</div>
            ) : isExternal ? (
              <a href={href} target="_blank" rel="noopener noreferrer" {...commonProps}>{inner}</a>
            ) : (
              <Link to={href} {...commonProps}>{inner}</Link>
            )}
          </div>
        );
      })}
    </div>
  );
});



/* =========================================================================
   HERO — title (3 stacked lines) + description + 6 CTAs + mockup image
   ========================================================================= */
/* =========================================================================
   CHAMPION BADGE — pink starburst with trophy ("TORNA-TE O CAMPEÃO …")
   Floats on the top-right corner of the hero. Fully editable.
   ========================================================================= */
const ChampionBadge = React.memo(function ChampionBadge({ cfg, lang }) {
  const b = cfg.champion_badge || {};
  // Render only when the admin has explicitly enabled the badge. Any other
  // value (undefined during first paint before config arrives, `false`, or
  // `null`) hides it — this prevents the "disabled" badge from flashing on
  // page load while the config is still being fetched.
  if (b.enabled !== true) return null;
  const Icon = ICONS[b.icon] || Trophy;
  const size = b.size_px || 220;
  const l1 = b[`line1_${lang}`] || b.line1_pt || "TORNA-TE";
  const l2 = b[`line2_${lang}`] || b.line2_pt || "O CAMPEÃO";
  const l3 = b[`line3_${lang}`] || b.line3_pt || "SUPERTMATIK!";

  // Sharp saw-blade starburst — geometry fully configurable from admin.
  const pts = Math.max(8, Math.min(60, b.spikes ?? 40));
  const rOut = 50;
  const rIn  = Math.max(20, Math.min(48, 50 - (b.spike_depth ?? 16)));
  const path = Array.from({ length: pts * 2 }, (_, i) => {
    const a = (Math.PI * i) / pts;
    const r = i % 2 === 0 ? rOut : rIn;
    return [50 + r * Math.cos(a), 50 + r * Math.sin(a)];
  }).map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ") + " Z";
  // corner_round (0..15) — fakes rounded tips by stroking the path with
  // its own fill color & linejoin=round. 0 → razor-sharp.
  const cornerRound = Math.max(0, Math.min(15, b.corner_round ?? 0));
  // Border / outline (separate from corner rounding — uses its own color).
  const borderColor = b.border_color || "#0A0E2A";
  const borderWidth = Math.max(0, Math.min(8, b.border_width ?? 0));
  // Drop-shadow (CSS filter on the wrapping element so it follows the path).
  const shX = b.shadow_x ?? 0;
  const shY = b.shadow_y ?? 10;
  const shBlur = b.shadow_blur ?? 40;
  const shAlpha = Math.max(0, Math.min(100, b.shadow_opacity ?? 60)) / 100;
  // Convert hex to rgba so opacity is independent of the color picker.
  const hexToRgba = (hex, a) => {
    const h = String(hex || "#000").replace("#", "");
    const n = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), bl = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${bl},${a})`;
  };
  const shadowCss = `drop-shadow(${shX}px ${shY}px ${shBlur}px ${hexToRgba(b.shadow_color || "#FF1A8C", shAlpha)})`;
  // Inner shadow — applied via SVG <filter> inside the path itself.
  const innerAlpha = Math.max(0, Math.min(100, b.inner_shadow_opacity ?? 0)) / 100;
  const innerOn = innerAlpha > 0;
  const innerX = b.inner_shadow_x ?? 0;
  const innerY = b.inner_shadow_y ?? 2;
  const innerBlur = b.inner_shadow_blur ?? 2;
  const innerColor = b.inner_shadow_color || "#000000";
  // Unique filter id per badge instance (avoid collisions on the page).
  const innerId = `cb-inner-${size}-${pts}-${rIn}`;

  const fillColor = b.bg_color || "#FF1A8C";
  const textColor = b.text_color || "#FFFFFF";
  const yellow    = b.icon_color || "#FFD93D";
  const fontFam   = b.font_family || "Fredoka";
  const rotation  = b.rotation_deg ?? -10;
  // Extended shimmer catalogue:
  //   shimmer         — conic rotating sweep (default)
  //   shimmer-sweep   — diagonal light bar that slides across
  //   shimmer-radial  — expanding radial pulses of light
  //   shimmer-holo    — holographic rainbow shift
  //   shimmer-twinkle — twinkling star bursts across the whole surface
  //   shimmer-lightning — quick flash streaks
  //   pulse | spin | none
  const effect    = b.effect || "shimmer";

  // Per-line rotation matches the reference (each label tilts independently).
  const r1 = b.line1_rotation ?? -6;
  const r2 = b.line2_rotation ?? -2;
  const r3 = b.line3_rotation ??  6;

  return (
    <div
      className="absolute z-10 select-none"
      style={{
        // On mobile the fixed `size` (default 220px) covered half the viewport
        // and overlapped the hero title. Clamp to a fraction of viewport so it
        // scales down naturally on small screens.
        // `--cb-size` exposes the effective badge dimension so font-sizes
        // and gaps below scale proportionally (previously they used the raw
        // `size` in px and stayed HUGE on mobile → text spilled outside star).
        // `--cb-rotation` mirrors the config-driven rotation so mobile CSS
        // can re-apply the exact SAME angle when it drops the desktop
        // `translate(-50%,-50%)` needed for corner-anchored placement.
        "--cb-size": `min(${size}px, 30vw)`,
        "--cb-rotation": `${rotation}deg`,
        width: "var(--cb-size)",
        height: "var(--cb-size)",
        left: `${b.position_x ?? 92}%`,
        top:  `${b.position_y ?? 12}%`,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
      }}
      data-testid="champion-badge"
    >
      {/* Inline keyframes — scoped to this badge instance via unique class. */}
      <style>{`
        @keyframes cb-shimmer { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
        @keyframes cb-shimmer-sweep {
          0%   { transform: translateX(-140%) skewX(-18deg); }
          100% { transform: translateX(140%)  skewX(-18deg); }
        }
        @keyframes cb-shimmer-radial {
          0%,100% { transform: scale(0.6); opacity: 0.05; }
          50%     { transform: scale(1.15); opacity: 0.6; }
        }
        @keyframes cb-shimmer-holo {
          0%   { filter: hue-rotate(0deg) saturate(1.15); }
          100% { filter: hue-rotate(360deg) saturate(1.15); }
        }
        @keyframes cb-shimmer-twinkle {
          0%,100% { opacity: 0.15; }
          25%     { opacity: 0.55; }
          50%     { opacity: 0.85; }
          75%     { opacity: 0.4;  }
        }
        @keyframes cb-shimmer-lightning {
          0%,88%,100% { opacity: 0; }
          90%,94%     { opacity: 0.75; }
          92%         { opacity: 0.15; }
        }
        @keyframes cb-pulse   { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }
        @keyframes cb-spin    { 0%{transform:rotate(${rotation}deg)} 100%{transform:rotate(${rotation+360}deg)} }
        @keyframes cb-glow    { 0%,100%{filter:drop-shadow(0 0 18px ${fillColor})} 50%{filter:drop-shadow(0 0 36px ${fillColor})} }
        @keyframes cb-sparkle { 0%,100%{opacity:.2; transform:scale(.6)} 50%{opacity:1; transform:scale(1.1)} }
        @keyframes cb-bounce  { 0%,100%{transform:translateY(0)} 25%{transform:translateY(-8%)} 50%{transform:translateY(0)} 75%{transform:translateY(-4%)} }
        @keyframes cb-wobble  { 0%,100%{transform:rotate(${rotation}deg)} 25%{transform:rotate(${rotation-6}deg)} 75%{transform:rotate(${rotation+6}deg)} }
        @keyframes cb-swing   { 0%,100%{transform:rotate(${rotation-10}deg)} 50%{transform:rotate(${rotation+10}deg)} }
        @keyframes cb-flash   { 0%,100%{filter:brightness(1)} 50%{filter:brightness(1.5) saturate(1.4)} }
        @keyframes cb-flicker { 0%,18%,22%,25%,53%,57%,100%{opacity:1} 20%,24%,55%{opacity:.6} }
        @keyframes cb-rainbow { 0%{filter:hue-rotate(0deg)} 100%{filter:hue-rotate(360deg)} }
        @keyframes cb-jump    { 0%,80%,100%{transform:scale(1)} 90%{transform:scale(1.15)} }
        @keyframes cb-rays    { 0%{transform:rotate(0) scale(1); opacity:.5} 100%{transform:rotate(360deg) scale(1.2); opacity:0} }
        @keyframes cb-heartbeat {
          0%,100%{transform:scale(1)} 14%{transform:scale(1.08)} 28%{transform:scale(1)} 42%{transform:scale(1.05)} 56%{transform:scale(1)}
        }
        @keyframes cb-tada {
          0%,100%{transform:scale(1) rotate(${rotation}deg)}
          10%,20%{transform:scale(.95) rotate(${rotation-3}deg)}
          30%,50%,70%,90%{transform:scale(1.08) rotate(${rotation+3}deg)}
          40%,60%,80%{transform:scale(1.08) rotate(${rotation-3}deg)}
        }
      `}</style>

      {/* Badge shape + animations */}
      <div
        className="absolute inset-0"
        style={{
          animation:
              effect === "pulse"     ? "cb-pulse 2.4s ease-in-out infinite"
            : effect === "spin"      ? "cb-spin 14s linear infinite"
            : effect === "bounce"    ? "cb-bounce 1.8s ease-in-out infinite"
            : effect === "wobble"    ? "cb-wobble 2.2s ease-in-out infinite"
            : effect === "swing"     ? "cb-swing 3s ease-in-out infinite"
            : effect === "flash"     ? "cb-flash 1.6s ease-in-out infinite"
            : effect === "flicker"   ? "cb-flicker 2.5s linear infinite"
            : effect === "rainbow"   ? "cb-rainbow 6s linear infinite"
            : effect === "jump"      ? "cb-jump 2.8s ease-in-out infinite"
            : effect === "heartbeat" ? "cb-heartbeat 1.5s ease-in-out infinite"
            : effect === "tada"      ? "cb-tada 3s ease-in-out infinite"
            : undefined,
        }}
      >
        {/* Pulsating glow halo under the badge */}
        <div
          className="absolute inset-[-8%]"
          style={{
            animation: "cb-glow 2.6s ease-in-out infinite",
            filter: `drop-shadow(0 0 22px ${fillColor})`,
            pointerEvents: "none",
          }}
          aria-hidden
        />
        <svg viewBox="0 0 100 100" className="w-full h-full relative" style={{ filter: shadowCss, overflow: "visible" }} aria-hidden>
          {innerOn && (
            <defs>
              <filter id={innerId} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation={innerBlur} />
                <feOffset dx={innerX} dy={innerY} result="offset-blur" />
                <feComposite operator="out" in="SourceAlpha" in2="offset-blur" result="inverse" />
                <feFlood floodColor={innerColor} floodOpacity={innerAlpha} result="color" />
                <feComposite operator="in" in="color" in2="inverse" result="shadow" />
                <feComposite operator="over" in="shadow" in2="SourceGraphic" />
              </filter>
            </defs>
          )}
          {/* Border / outline path — drawn first so the fill sits on top */}
          {borderWidth > 0 && (
            <path
              d={path}
              fill="none"
              stroke={borderColor}
              strokeWidth={borderWidth}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
          <path
            d={path}
            fill={fillColor}
            stroke={cornerRound > 0 ? fillColor : "none"}
            strokeWidth={cornerRound}
            strokeLinejoin="round"
            strokeLinecap="round"
            filter={innerOn ? `url(#${innerId})` : undefined}
          />
          {/* Shimmer sweep — conic gradient rotates over the badge surface. */}
          {effect === "shimmer" && (
            <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
              <div
                style={{
                  width: "100%", height: "100%",
                  background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.28) 50deg, transparent 110deg, transparent 360deg)",
                  mixBlendMode: "screen",
                  animation: "cb-shimmer 4.5s linear infinite",
                  transformOrigin: "50% 50%",
                }}
              />
            </foreignObject>
          )}
          {/* Shimmer-sweep — diagonal light bar sliding across the badge. */}
          {effect === "shimmer-sweep" && (
            <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
              <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)",
                    mixBlendMode: "screen",
                    animation: "cb-shimmer-sweep 3.2s ease-in-out infinite",
                  }}
                />
              </div>
            </foreignObject>
          )}
          {/* Shimmer-radial — pulsing radial glow from the centre outward. */}
          {effect === "shimmer-radial" && (
            <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
              <div
                style={{
                  width: "100%", height: "100%",
                  background: "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.1) 45%, transparent 70%)",
                  mixBlendMode: "screen",
                  animation: "cb-shimmer-radial 2.4s ease-in-out infinite",
                  transformOrigin: "50% 50%",
                }}
              />
            </foreignObject>
          )}
          {/* Shimmer-holo — rainbow hue-rotation overlay (holographic look). */}
          {effect === "shimmer-holo" && (
            <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
              <div
                style={{
                  width: "100%", height: "100%",
                  background: "linear-gradient(120deg, rgba(255,0,180,0.45), rgba(0,200,255,0.45), rgba(255,220,0,0.45), rgba(0,255,120,0.45), rgba(255,0,180,0.45))",
                  backgroundSize: "300% 300%",
                  mixBlendMode: "overlay",
                  animation: "cb-shimmer-holo 6s linear infinite",
                }}
              />
            </foreignObject>
          )}
          {/* Shimmer-twinkle — 6 sparkle points twinkling in and out. */}
          {effect === "shimmer-twinkle" && (
            <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
              <div style={{ position: "relative", width: "100%", height: "100%" }}>
                {[
                  { top: "20%", left: "25%", size: 8,  delay: "0s"   },
                  { top: "35%", left: "70%", size: 6,  delay: "0.4s" },
                  { top: "55%", left: "18%", size: 9,  delay: "0.9s" },
                  { top: "62%", left: "78%", size: 7,  delay: "1.3s" },
                  { top: "78%", left: "40%", size: 8,  delay: "1.7s" },
                  { top: "28%", left: "50%", size: 5,  delay: "2.1s" },
                ].map((s) => (
                  <div key={`tw-${s.top}-${s.left}`} style={{
                    position: "absolute", top: s.top, left: s.left,
                    width: s.size, height: s.size, borderRadius: "50%",
                    background: "radial-gradient(circle, #FFFFFF 0%, rgba(255,255,255,0) 70%)",
                    animation: `cb-shimmer-twinkle 2.4s ease-in-out ${s.delay} infinite`,
                    mixBlendMode: "screen",
                  }} />
                ))}
              </div>
            </foreignObject>
          )}
          {/* Shimmer-lightning — quick flash bursts at random intervals. */}
          {effect === "shimmer-lightning" && (
            <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
              <div
                style={{
                  width: "100%", height: "100%",
                  background: "linear-gradient(60deg, transparent 40%, rgba(255,255,255,0.75) 50%, transparent 60%)",
                  mixBlendMode: "screen",
                  animation: "cb-shimmer-lightning 3.6s ease-out infinite",
                }}
              />
            </foreignObject>
          )}
        </svg>

        {/* Sparkle dots around the badge — 4 tiny white stars */}
        {effect !== "none" && (
          <>
            <div className="absolute" style={{ top: "8%", left: "12%", width: "10%", height: "10%", background: "white", clipPath: "polygon(50% 0,60% 40%,100% 50%,60% 60%,50% 100%,40% 60%,0 50%,40% 40%)", animation: "cb-sparkle 2.2s ease-in-out infinite", animationDelay: "0s" }} aria-hidden />
            <div className="absolute" style={{ top: "18%", right: "10%", width: "7%",  height: "7%",  background: "white", clipPath: "polygon(50% 0,60% 40%,100% 50%,60% 60%,50% 100%,40% 60%,0 50%,40% 40%)", animation: "cb-sparkle 2.6s ease-in-out infinite", animationDelay: ".4s" }} aria-hidden />
            <div className="absolute" style={{ bottom: "20%", left: "8%",  width: "8%",  height: "8%",  background: "white", clipPath: "polygon(50% 0,60% 40%,100% 50%,60% 60%,50% 100%,40% 60%,0 50%,40% 40%)", animation: "cb-sparkle 3.0s ease-in-out infinite", animationDelay: ".8s" }} aria-hidden />
            <div className="absolute" style={{ bottom: "12%", right: "18%", width: "6%",  height: "6%",  background: "white", clipPath: "polygon(50% 0,60% 40%,100% 50%,60% 60%,50% 100%,40% 60%,0 50%,40% 40%)", animation: "cb-sparkle 2.0s ease-in-out infinite", animationDelay: "1.2s" }} aria-hidden />
          </>
        )}
      </div>

      {/* ICON — positioned absolutely at the top-center of the badge so it
       *  does NOT offset the text vertical centering. */}
      {(b.icon_size ?? 22) > 0 && (
        <div
          className="absolute pointer-events-none flex justify-center"
          style={{
            top: `${b.icon_top_pct ?? 18}%`,
            left: `${b.icon_left_pct ?? 50}%`,
            transform: "translate(-50%, 0)",
          }}
        >
          <Icon
            style={{
              color: yellow,
              width: size * ((b.icon_size ?? 22) / 100),
              height: size * ((b.icon_size ?? 22) / 100),
              strokeWidth: 2.4,
              transform: `rotate(${b.icon_rotation ?? 0}deg)`,
              transformOrigin: "center",
            }}
            aria-hidden
          />
        </div>
      )}

      {/* TEXT — positioned via configurable anchor so admin can push it up/
       *  down/left/right independently of the icon. Default anchor is the
       *  visual centre (50%, 50%) which behaves like the previous version.
       */}
      <div
        className="absolute"
        style={{
          top: `${b.text_top_pct ?? 50}%`,
          left: `${b.text_left_pct ?? 50}%`,
          transform: "translate(-50%, -50%)",
          width: `calc(${rIn * 2}% - ${(b.text_padding ?? 16) * 2}%)`,
        }}
        aria-hidden
      >
        <div
          className="flex flex-col items-center justify-center uppercase leading-[1.05] text-center w-full"
          style={{
            fontFamily: `"${fontFam}", system-ui, sans-serif`,
          }}
        >
          <div
            style={{
              color: textColor,
              fontSize: `calc(var(--cb-size) * ${(b.line1_size ?? 8) / 100})`,
              fontWeight: b.line1_weight ?? 900,
              letterSpacing: "0.02em",
              textAlign: b.line1_align || "center",
              transform: `translateX(${b.line1_offset_x ?? 0}%) rotate(${r1}deg)`,
              transformOrigin: "center",
              display: "inline-block",
              marginTop: `calc(var(--cb-size) * ${(b.line1_gap ?? 0) / 100})`,
            }}
          >{l1}</div>
          <div
            style={{
              color: textColor,
              fontSize: `calc(var(--cb-size) * ${(b.line2_size ?? 9) / 100})`,
              fontWeight: b.line2_weight ?? 900,
              letterSpacing: "0.02em",
              textAlign: b.line2_align || "center",
              transform: `translateX(${b.line2_offset_x ?? 0}%) rotate(${r2}deg)`,
              transformOrigin: "center",
              marginTop: `calc(var(--cb-size) * ${(b.line2_gap ?? 0.5) / 100})`,
              display: "inline-block",
            }}
          >{l2}</div>
          <div
            style={{
              color: yellow,
              fontSize: `calc(var(--cb-size) * ${(b.line3_size ?? 11) / 100})`,
              fontWeight: b.line3_weight ?? 900,
              letterSpacing: "0.01em",
              lineHeight: 1,
              textAlign: b.line3_align || "center",
              transform: `translateX(${b.line3_offset_x ?? 0}%) rotate(${r3}deg)`,
              transformOrigin: "center",
              marginTop: `calc(var(--cb-size) * ${(b.line3_gap ?? 1.5) / 100})`,
              display: "inline-block",
            }}
          >
            {l3}
          </div>
        </div>
      </div>
    </div>
  );
});

/*
 * TabletDemoFrame — fits the real in-site DEMO inside the tablet overlay.
 *
 * Design:
 *   • The outer rectangle is positioned over the mascot image via the
 *     admin-configured percentages (x/y/w/h).
 *   • Inside, a bezel + inner "screen" wrapper simulate a real tablet
 *     frame (configurable thickness, color, shadow, highlight glare,
 *     notch camera dot).
 *   • The iframe runs the actual GamePlay in embed mode. It is given a
 *     virtual viewport whose ASPECT RATIO matches the screen area, so the
 *     game lays out naturally with every element visible. We then scale
 *     uniformly to fill the screen area (no clipping of controls/cards).
 *   • When the admin picks `fit="stretch"`, we scale non-uniformly so the
 *     content fills exactly, slightly deforming the layout.
 *
 * Immediate render: the iframe is mounted on the very first render using
 * a conservative initial scale estimate; the ResizeObserver then refines
 * it in-place without any black-screen flash.
 */
const TABLET_DEMO_REF_WIDTH = 1280; // reference virtual width in px

// Converts #RRGGBB (or #RGB) to `rgba(r,g,b,alpha)` safely. Falls back to the
// hex string unchanged if parsing fails — colours are already valid CSS.
function hexWithAlpha(hex, alpha) {
  if (!hex || typeof hex !== "string") return `rgba(0,0,0,${alpha})`;
  let v = hex.replace("#", "");
  if (v.length === 3) v = v.split("").map((c) => c + c).join("");
  if (v.length !== 6) return hex;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/*
 * perspectiveMatrix3d — builds a CSS matrix3d string that maps the
 * axis-aligned source rect [0,0]–[W,H] onto the arbitrary quadrilateral
 * (p0=TL, p1=TR, p2=BR, p3=BL) in local-pixel coords. Used to simulate
 * the tablet's perspective by freely dragging the 4 corner handles in
 * the admin.
 *
 * Formula adapted from Blinn ("Hyperbolic Interpolation", 1992) — unit
 * square to quad — composed with a pre-scale by (1/W, 1/H) so we can
 * feed the rect dimensions directly.
 */
function perspectiveMatrix3d(W, H, p0, p1, p2, p3) {
  if (!W || !H) return "none";
  const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x;
  const dy1 = p1.y - p2.y, dy2 = p3.y - p2.y;
  const sx = p0.x - p1.x + p2.x - p3.x;
  const sy = p0.y - p1.y + p2.y - p3.y;
  const det = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(det) < 1e-10) return "none";
  const g = (sx * dy2 - dx2 * sy) / det;
  const h = (dx1 * sy - sx * dy1) / det;
  // Unit-square → quad matrix.
  const a = p1.x - p0.x + g * p1.x;
  const b = p3.x - p0.x + h * p3.x;
  const c = p0.x;
  const d = p1.y - p0.y + g * p1.y;
  const e = p3.y - p0.y + h * p3.y;
  const f = p0.y;
  // Pre-scale by (1/W, 1/H) by dividing the first two columns.
  const A = a / W, B = b / H;
  const D = d / W, E = e / H;
  const G = g / W, Hc = h / H;
  // CSS matrix3d column-major.
  return `matrix3d(${A},${D},0,${G}, ${B},${E},0,${Hc}, 0,0,1,0, ${c},${f},0,1)`;
}

function TabletSparkles({ thickness, radius, color }) {
  // 10 twinkling star dots sprinkled around the frame; positions are
  // deterministic (seeded by index) so React doesn't reshuffle every render.
  const stars = React.useMemo(() => {
    const out = [];
    for (let i = 0; i < 10; i++) {
      const seed = (i * 2654435761) % 1000 / 1000;
      const side = i % 4; // 0=top,1=right,2=bottom,3=left
      const alongPct = ((seed * 1000) % 100);
      const away = (((i + 1) * 47) % thickness) + 2;
      let left, top;
      if (side === 0) { left = `${alongPct}%`; top = `${-away}px`; }
      else if (side === 1) { left = `calc(100% + ${away}px)`; top = `${alongPct}%`; }
      else if (side === 2) { left = `${alongPct}%`; top = `calc(100% + ${away}px)`; }
      else { left = `${-away}px`; top = `${alongPct}%`; }
      out.push({ left, top, size: 4 + ((i * 3) % 7), delay: (i * 0.18) % 1.8 });
    }
    return out;
  }, [thickness]);
  return (
    <div className="absolute pointer-events-none" style={{ inset: 0, zIndex: 4 }} data-testid="hero-tablet-fx-sparkles">
      {stars.map((s, idx) => (
        <div
          key={idx}
          className="sm-tablet-sparkle"
          style={{
            position: "absolute",
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
            boxShadow: `0 0 ${s.size * 2}px ${color}`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

function TabletDemoFrame({
  src, videoSrc, xPct, yPct, wPct, hPct, rotate, radius,
  bezel, bezelColor, shadow, glare, notch, fit, posterUrl, langKey,
  glow, glowColor, glowPulse, innerShadow, scanlines, tilt, tiltAxis,
  cornerOffsets, // { tl: {dx,dy}, tr: ..., br: ..., bl: ... } in container-%
  fxNeonRing, fxRainbow, fxSparkles, fxThickness,
  demoAlign = "right", // horizontal alignment of the demo inside the screen
}) {
  const rectRef = React.useRef(null);
  // Pre-paint with a non-zero scale so the first frame is NOT a black box.
  // ResizeObserver refines the exact scale on the next layout tick.
  const [rect, setRect] = React.useState({ w: 320, h: 220 });
  const [iframeReady, setIframeReady] = React.useState(false);
  // Transition phases:
  //   "image"  → hero image stays 100% intact (overlay fully transparent)
  //   "static" → brief "TV sem antena" static (só riscos) once demo is ready
  //   "live"   → the real demo is shown
  // The screen NEVER paints black/opaque before the demo is ready.
  const [phase, setPhase] = React.useState("image");
  React.useEffect(() => {
    if (!iframeReady) return;
    setPhase("static");
    const t = setTimeout(() => setPhase("live"), 850);
    return () => clearTimeout(t);
  }, [iframeReady]);
  // Re-arm the transition whenever the source/language changes (new demo load).
  React.useEffect(() => {
    setIframeReady(false);
    setPhase("image");
  }, [src, videoSrc, langKey]);

  React.useLayoutEffect(() => {
    const el = rectRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      // Inner screen dims — subtract bezel from both sides.
      const w = Math.max(1, r.width - bezel * 2);
      const h = Math.max(1, r.height - bezel * 2);
      setRect({ w, h });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [bezel]);

  // The in-site demo is FULLY RESPONSIVE — it lays itself out to fill whatever
  // viewport aspect it is given (exactly like the standalone demo window). So
  // we size the virtual viewport to the SAME aspect as the defined screen rect:
  // the demo then fills it completely, showing EVERY element with no cropping
  // and no empty bands. (The old fixed-4:3 / MIN_VH logic distorted the aspect
  // and cropped the right-hand side — timer, options, player card.)
  const aspect = rect.h / Math.max(1, rect.w); // height / width of the screen
  let vw = TABLET_DEMO_REF_WIDTH;               // 1280 → desktop (not mobile) layout
  let vh = Math.max(1, Math.round(vw * aspect));
  let scaleX, scaleY;
  if (fit === "stretch") {
    scaleX = rect.w / vw;
    scaleY = rect.h / vh;
  } else {
    // Uniform scale. Because vh === vw*aspect this fills the rect exactly.
    const s = rect.w / vw;
    scaleX = s;
    scaleY = s;
  }
  const scaledW = vw * scaleX;
  const scaledH = vh * scaleY;
  const slackW = Math.max(0, rect.w - scaledW);
  const slackH = Math.max(0, rect.h - scaledH);
  const demoLeftPx = demoAlign === "left" ? 0 : demoAlign === "center" ? slackW / 2 : slackW;
  const demoTopPx = slackH / 2; // vertically centred within the screen area

  const outerShadow = shadow > 0
    ? `0 ${Math.round(shadow * 0.4)}px ${Math.round(shadow * 1.2)}px rgba(0,0,0,${Math.min(0.65, shadow / 90)})`
    : "none";
  // Combine outer drop shadow with the colored glow halo if any.
  const glowRgba = glow > 0 ? hexWithAlpha(glowColor || "#3DA5FF", (glow / 100) * 0.9) : null;
  const glowShadow = glow > 0
    ? `0 0 ${Math.round((glow / 100) * 30)}px ${Math.round((glow / 100) * 6)}px ${glowRgba}, 0 0 ${Math.round((glow / 100) * 60)}px ${Math.round((glow / 100) * 12)}px ${hexWithAlpha(glowColor || "#3DA5FF", (glow / 100) * 0.45)}`
    : "";
  const combinedShadow = [outerShadow !== "none" ? outerShadow : "", glowShadow].filter(Boolean).join(", ") || "none";
  const tiltTransform = tilt && tilt !== 0
    ? (tiltAxis === "y" ? ` rotateY(${tilt}deg)` : ` rotateX(${-tilt}deg)`)
    : "";

  // ───── Perspective: free 4-corner warp driven by the admin editor ─────
  // Offsets are stored in CONTAINER-% (same units as xPct/wPct). Here we
  // convert them to LOCAL coords (both % for the clip-path on the outer
  // wrapper, and pixels for the matrix3d applied to the inner content).
  const co = cornerOffsets || { tl: { dx: 0, dy: 0 }, tr: { dx: 0, dy: 0 }, br: { dx: 0, dy: 0 }, bl: { dx: 0, dy: 0 } };
  const hasPerspective = ["tl", "tr", "br", "bl"].some((k) => (co[k]?.dx || 0) !== 0 || (co[k]?.dy || 0) !== 0);
  // Local-% offsets (safe-guard: don't divide by zero if the rect degenerates).
  const safeW = wPct > 0 ? wPct : 1;
  const safeH = hPct > 0 ? hPct : 1;
  const toLocalPct = (p) => ({ dxp: (p.dx || 0) / safeW * 100, dyp: (p.dy || 0) / safeH * 100 });
  const tlLp = toLocalPct(co.tl || {});
  const trLp = toLocalPct(co.tr || {});
  const brLp = toLocalPct(co.br || {});
  const blLp = toLocalPct(co.bl || {});
  const tlPt = { x: 0 + tlLp.dxp,   y: 0 + tlLp.dyp };
  const trPt = { x: 100 + trLp.dxp, y: 0 + trLp.dyp };
  const brPt = { x: 100 + brLp.dxp, y: 100 + brLp.dyp };
  const blPt = { x: 0 + blLp.dxp,   y: 100 + blLp.dyp };
  const clipPolygon = hasPerspective
    ? `polygon(${tlPt.x}% ${tlPt.y}%, ${trPt.x}% ${trPt.y}%, ${brPt.x}% ${brPt.y}%, ${blPt.x}% ${blPt.y}%)`
    : "none";
  // Target pixels for the inner-content matrix3d (relative to the rect box).
  const Wpx = rect.w, Hpx = rect.h;
  const toPx = (lp, baseX, baseY) => ({ x: baseX + (lp.dxp / 100) * Wpx, y: baseY + (lp.dyp / 100) * Hpx });
  const warpMatrix = hasPerspective
    ? perspectiveMatrix3d(Wpx, Hpx,
        toPx(tlLp, 0,   0),
        toPx(trLp, Wpx, 0),
        toPx(brLp, Wpx, Hpx),
        toPx(blLp, 0,   Hpx),
      )
    : "none";

  return (
    <div
      style={{
        position: "absolute",
        left: `${xPct}%`,
        top: `${yPct}%`,
        width: `${wPct}%`,
        height: `${hPct}%`,
        perspective: tilt ? "900px" : undefined,
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      {/* Impactful FX halos sitting BEHIND the frame — extended outwards
          by `fxThickness` so they look like a glow aura around the ecrã. */}
      {(fxNeonRing || fxRainbow) && (
        <div
          className={`absolute ${fxNeonRing ? "sm-tablet-neon-ring" : "sm-tablet-rainbow"}`}
          style={{
            inset: -(fxThickness || 20),
            borderRadius: Math.max(0, radius + (fxThickness || 20)),
            "--sm-ring": glowColor || "#3DA5FF",
            zIndex: -1,
            pointerEvents: "none",
          }}
          data-testid="hero-tablet-fx-aura"
        />
      )}
      {fxSparkles && (
        <TabletSparkles thickness={fxThickness || 20} radius={radius} color={glowColor || "#FFD86B"} />
      )}
      <div
        ref={rectRef}
        className={glow > 0 && glowPulse ? "sm-pulse-glow" : ""}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          transform: `rotate(${rotate}deg)${tiltTransform}`,
          transformOrigin: "center center",
          borderRadius: radius,
          // Transparent (no black/colour bezel) until the demo is live, so the
          // lateral hero image stays fully intact behind the overlay.
          background: phase === "live" ? (bezelColor || "#111") : "transparent",
          padding: `${bezel}px`,
          boxShadow: phase === "live" ? combinedShadow : "none",
          overflow: hasPerspective ? "visible" : "hidden",
          clipPath: hasPerspective ? clipPolygon : undefined,
        }}
        data-testid="hero-tablet-demo-wrap"
      >
      {/* Notch / front camera */}
      {notch && (
        <div
          className="absolute left-1/2"
          style={{
            top: Math.max(2, bezel / 3),
            transform: "translateX(-50%)",
            width: Math.max(4, bezel * 0.6),
            height: Math.max(4, bezel * 0.6),
            borderRadius: "50%",
            background: "radial-gradient(circle at 30% 30%, #4b5563 0%, #0b0b0b 70%)",
            boxShadow: "inset 0 0 2px rgba(255,255,255,0.4)",
          }}
        />
      )}

      {/* Inner screen area + iframe */}
      <div
        className="relative w-full h-full overflow-hidden"
        style={{
          borderRadius: Math.max(0, radius - bezel),
          // ALWAYS transparent so the lateral hero image (the tablet in the
          // photo) stays 100% intact until the demo is ready. The screen never
          // flashes black or any solid colour before the demo arrives.
          background: "transparent",
          transform: hasPerspective ? warpMatrix : undefined,
          transformOrigin: "0 0",
        }}
      >
        {/* Optional poster: only drawn while the image phase is active, kept
            transparent-backed and "contain" so it never introduces a black
            box. If no poster, the underlying hero image simply shows through. */}
        {posterUrl && phase !== "live" && (
          <img
            src={posterUrl.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${posterUrl}` : posterUrl}
            alt="demo"
            className="absolute inset-0 w-full h-full"
            style={{
              objectFit: fit === "stretch" ? "fill" : "contain",
              background: "transparent",
              opacity: phase === "image" ? 1 : 0,
              transition: "opacity 400ms ease",
              zIndex: 1,
              pointerEvents: "none",
            }}
            data-testid="hero-tablet-poster"
          />
        )}
        {videoSrc ? (
          <video
            key={langKey}
            src={videoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onCanPlay={() => setIframeReady(true)}
            onPlaying={() => setIframeReady(true)}
            className={`block w-full h-full ${fit === "stretch" ? "" : "object-contain"}`}
            style={{
              objectFit: fit === "stretch" ? "fill" : "contain",
              objectPosition: demoAlign === "left" ? "left center" : demoAlign === "center" ? "center" : "right center",
              background: "transparent",
              display: "block",
              opacity: phase === "live" ? 1 : 0,
              transition: "opacity 450ms ease",
            }}
            data-testid="hero-tablet-video"
          />
        ) : (
          <iframe
            key={langKey}
            title="demo-tablet"
            src={src}
            scrolling="no"
            loading="eager"
            onLoad={() => setIframeReady(true)}
            className="block border-0"
            style={{
              position: "absolute",
              left: `${demoLeftPx}px`,
              top: `${demoTopPx}px`,
              width: `${vw}px`,
              height: `${vh}px`,
              transform: `scale(${scaleX}, ${scaleY})`,
              transformOrigin: "top left",
              // Transparent background + hidden until the demo is actually ready,
              // so the hero image is what shows through while it bootstraps.
              background: "transparent",
              pointerEvents: "none",
              display: "block",
              opacity: phase === "live" ? 1 : 0,
              transition: "opacity 450ms ease",
            }}
            data-testid="hero-tablet-demo"
            allow="autoplay"
          />
        )}
        {/* "TV sem antena" static — the transition between the intact image
            and the live demo. Only grey noise + horizontal riscos. Fades out
            as the demo takes over. */}
        <div
          aria-hidden
          className={phase === "static" ? "sm-tv-static" : ""}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            borderRadius: Math.max(0, radius - bezel),
            opacity: phase === "static" ? 1 : 0,
            transition: "opacity 450ms ease",
            pointerEvents: "none",
          }}
          data-testid="hero-tablet-static"
        />
        {/* Glare / highlight overlay */}
        {glare > 0 && (
          <div
            className="absolute inset-0"
            style={{
              background:
                `linear-gradient(135deg, rgba(255,255,255,${(glare / 100) * 0.35}) 0%, rgba(255,255,255,0) 45%, rgba(255,255,255,0) 55%, rgba(255,255,255,${(glare / 100) * 0.12}) 100%)`,
              pointerEvents: "none",
              mixBlendMode: "screen",
              zIndex: 3,
            }}
          />
        )}
        {/* Inner shadow / vignette */}
        {innerShadow > 0 && (
          <div
            className="absolute inset-0"
            style={{
              boxShadow: `inset 0 0 ${Math.round((innerShadow / 100) * 70)}px ${Math.round((innerShadow / 100) * 25)}px rgba(0,0,0,${Math.min(0.9, innerShadow / 100 * 0.9)})`,
              borderRadius: Math.max(0, radius - bezel),
              pointerEvents: "none",
              zIndex: 4,
            }}
          />
        )}
        {/* Scanlines */}
        {scanlines > 0 && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(0deg, rgba(0,0,0,${(scanlines / 100) * 0.35}) 0px, rgba(0,0,0,${(scanlines / 100) * 0.35}) 1px, transparent 1px, transparent 3px)`,
              mixBlendMode: "multiply",
              pointerEvents: "none",
              zIndex: 5,
            }}
          />
        )}
      </div>
      </div>
    </div>
  );
}


const Hero = React.memo(function Hero({ cfg, lang }) {
  const titleLines = (pick(cfg.hero, "title", lang) || "").split("\n");
  const titleColors = cfg.hero?.title_colors || ["#FFFFFF", "#3DA5FF", "#E5FF00"];
  const titleGlow = cfg.hero?.title_glow || "#7DEAFF";
  const desc = pick(cfg.hero, "description", lang);
  const hlWord = cfg.hero?.description_highlight_word || "SUPERTMATIK";
  const hlColor = cfg.hero?.description_highlight_color || "#39FF14";

  // Tablet overlay fallback — when no custom MP4 is uploaded for the active
  // language, we embed the actual in-site DEMO ("Aprender a Jogar") inside
  // the tablet. We only need any active training game to drive the demo —
  // the demo engine is self-contained and does not record results.
  const [demoGameId, setDemoGameId] = React.useState(null);
  // Side video failed to load/decode → fall back to the static side image.
  const [sideVideoFailed, setSideVideoFailed] = React.useState(false);
  const sideVideoActiveUrl = cfg.hero?.side_video_enabled !== false
    ? (cfg.hero?.[`side_video_url_${lang}`] || cfg.hero?.side_video_url_pt || "")
    : "";
  React.useEffect(() => { setSideVideoFailed(false); }, [sideVideoActiveUrl]);
  // Natural aspect of the side image — lets the video box occupy exactly the
  // same area the image used to (side_video_aspect === "image").
  const [sideImgRatio, setSideImgRatio] = React.useState(null);
  const sideImageUrlRaw = cfg.hero?.side_image_url || "";
  React.useEffect(() => {
    if (!sideImageUrlRaw) { setSideImgRatio(null); return; }
    const im = new Image();
    im.onload = () => { if (im.naturalWidth > 1 && im.naturalHeight > 1) setSideImgRatio(`${im.naturalWidth} / ${im.naturalHeight}`); };
    im.src = sideImageUrlRaw.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${sideImageUrlRaw}` : sideImageUrlRaw;
  }, [sideImageUrlRaw]);
  const sideVideoCustomGeom = (cfg.hero?.side_video_geometry || "image") === "custom";
  // Live box of the side video (relative to the hero grid) for the store overlay.
  const heroGridRef = React.useRef(null);
  const [videoRect, setVideoRect] = React.useState(null);
  const storeOverlayOn = !!(sideVideoActiveUrl && !sideVideoFailed && cfg.hero?.[`side_video_store_enabled_${lang}`]);
  React.useEffect(() => {
    if (!storeOverlayOn) { setVideoRect(null); return undefined; }
    let raf = 0;
    let last = "";
    const tick = () => {
      const grid = heroGridRef.current;
      const v = grid && grid.querySelector("[data-testid='hero-side-video']");
      if (grid && v) {
        const g = grid.getBoundingClientRect();
        const r = v.getBoundingClientRect();
        const next = { left: Math.round(r.left - g.left), top: Math.round(r.top - g.top), bottom: Math.round(r.bottom - g.top), width: Math.round(r.width), height: Math.round(r.height) };
        const key = `${next.left}|${next.top}|${next.bottom}|${next.width}`;
        if (key !== last) { last = key; setVideoRect(next); }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [storeOverlayOn, sideVideoActiveUrl]);
  React.useEffect(() => {
    let alive = true;
    api.get("/games", { params: { status: "training", active_only: true } })
      .then((r) => {
        if (!alive) return;
        const list = r?.data || [];
        if (list.length) setDemoGameId(list[0].id);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Render the description with the highlight word stroked in neon.
  const descParts = desc && hlWord ? desc.split(new RegExp(`(${hlWord})`, "i")) : [desc];

  return (
    <section className="relative overflow-visible">
      <ChampionBadge cfg={cfg} lang={lang} />

      <div ref={heroGridRef} className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-6 items-end pt-8 pb-4 relative">
        {/* LEFT: title + desc + CTAs */}
        <div
          className="lg:col-span-5 space-y-5 relative overflow-visible"
          style={{ zIndex: cfg.hero?.title_image_z ?? 3 }}
          data-testid="hero-content"
        >
          {(() => {
            // Pick the AI painted title matching the active language, with
            // graceful fallback to the shared `title_image_url` when no
            // per-language variant is configured yet.
            const titleUrl = cfg.hero?.[`title_image_url_${lang}`]
              || cfg.hero?.title_image_url
              || "";
            return titleUrl ? (
            <img
              src={titleUrl.startsWith("/")
                ? `${process.env.REACT_APP_BACKEND_URL || ""}${titleUrl}`
                : titleUrl}
              alt="APRENDE. JOGA. VENCE!"
              className="w-full max-w-full h-auto"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width={cfg.hero?.title_image_width || 560}
              height={Math.round(((cfg.hero?.title_image_width || 560) * 9) / 16)}
              style={{
                // On mobile the AI title image (up to 780px wide) was
                // overflowing the viewport and appearing giant. Cap at 100%
                // of the parent width and let scale still work but only within
                // the available column area.
                maxWidth: `min(100%, ${cfg.hero?.title_image_width || 560}px)`,
                marginLeft: -8,
                transform: `translate(${cfg.hero?.title_image_offset_x || 0}px, ${cfg.hero?.title_image_offset_y || 0}px) scale(${cfg.hero?.title_image_scale ?? 1}) rotate(${cfg.hero?.title_image_rotate || 0}deg)`,
                transformOrigin: "left center",
                filter: `drop-shadow(0 20px 40px ${cfg.hero?.title_image_glow_color || "#000000"}${Math.round((cfg.hero?.title_image_glow_intensity ?? 55) * 2.55).toString(16).padStart(2, "0")})`,
                zIndex: cfg.hero?.title_image_z ?? 3,
                position: "relative",
                opacity: (cfg.hero?.title_image_opacity ?? 100) / 100,
              }}
              data-testid="hero-title-image"
            />
          ) : (
          <h1 className="font-black tracking-tight leading-[0.92]" style={{ fontFamily: '"Bowlby One SC", "Fredoka", sans-serif' }}>
            {titleLines.map((line, i) => {
              const color = titleColors[i] || titleColors[titleColors.length - 1] || "#FFFFFF";
              // Slight per-line skew/rotation to mimic the hand-painted feel
              // of the reference (each word leans a bit differently).
              const skew = [-1.5, 0, -2][i] || 0;
              return (
                <div
                  key={`title-${line}-${i}`}
                  className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[112px]"
                  style={{
                    color,
                    transform: `skewY(${skew}deg)`,
                    // Layered text-shadow: thick dark base for chunky depth +
                    // distant neon halo for grunge/spray-paint atmosphere.
                    textShadow: `
                      0 0 22px ${titleGlow}40,
                      0 0 44px ${titleGlow}25,
                      4px 6px 0 rgba(0,0,0,0.55),
                      -1px -1px 0 rgba(255,255,255,0.15),
                      0 12px 30px rgba(0,0,0,0.45)
                    `,
                    WebkitTextStroke: "1.5px rgba(0,0,0,0.25)",
                    letterSpacing: "-0.02em",
                  }}
                  data-testid={`hero-title-line-${i}`}
                >
                  {line}
                </div>
              );
            })}
          </h1>
          );
          })()}
          {sideVideoActiveUrl && !sideVideoFailed && <SideVideoStoreButton h={cfg.hero || {}} lang={lang} variant="mobile" />}
          <p className="text-base sm:text-lg leading-relaxed whitespace-pre-line" style={{ color: "rgba(255,255,255,0.88)", width: (cfg.cta_layout?.max_width ?? 660), maxWidth: "none", transform: `translateY(${cfg.hero?.description_offset_y ?? 0}px)` }} data-testid="hero-description">
            {descParts.map((p, i) =>
              p && p.toUpperCase() === hlWord.toUpperCase()
                ? <span key={`desc-hl-${i}`} className="font-black" style={{ color: hlColor, textShadow: `0 0 12px ${hlColor}80` }}>{p}</span>
                : <span key={`desc-${i}`}>{p}</span>
            )}
          </p>

          {/* CTAs — premium mobile-game grid (4:1 buttons, 210×52, uniform). */}
          {(() => {
            const L = cfg.cta_layout || {};
            return (
              <div
                className="flex flex-col"
                style={{
                  width: L.max_width ?? 660,
                  maxWidth: "none",
                  gap: L.row_gap ?? 10,
                  transform: `translateY(${cfg.hero?.ctas_offset_y ?? 0}px)`,
                }}
                data-testid="cta-grid"
              >
                <CtaRow
                  items={cfg.ctas_primary || []}
                  lang={lang}
                  layout={L}
                  height={L.primary_height ?? 52}
                  textSize={L.text_primary || undefined}
                  iconRotate={L.icon_rotate_primary ?? 0}
                  textRotate={L.text_rotate_primary ?? 0}
                />
                <CtaRow
                  items={cfg.ctas_secondary || []}
                  lang={lang}
                  layout={L}
                  height={L.secondary_height ?? 52}
                  textSize={L.text_secondary || undefined}
                  iconRotate={L.icon_rotate_secondary ?? 0}
                  textRotate={L.text_rotate_secondary ?? 0}
                />
              </div>
            );
          })()}
        </div>

        {/* CENTER: SUPERTMATIK mascot character + subtitle */}
        <div
          className="lg:col-span-3 relative h-full min-h-[300px] lg:min-h-[600px] flex flex-col items-center justify-center overflow-visible"
          style={{
            zIndex: cfg.hero?.center_image_z ?? 10,
            // Decorative image only — never intercept mouse events. Without
            // this the wrapper (which spans a wide `lg:col-span-3` cell)
            // overlaps the CTA column to the left/right in some viewport
            // sizes and blocks hover/clicks on the 3rd-column CTAs. The
            // image itself is not interactive, so passing pointer events
            // through is safe.
            pointerEvents: "none",
          }}
          data-testid="hero-mascot"
        >
          {cfg.hero?.center_image_url && (
            <div
              className="relative flex flex-col items-center"
              style={{
                width: cfg.hero?.center_image_width || 480,
                transform: `translate(${cfg.hero?.center_image_offset_x || 0}px, ${cfg.hero?.center_image_offset_y || 0}px) scale(${cfg.hero?.center_image_scale ?? 1}) rotate(${cfg.hero?.center_image_rotate || 0}deg)`,
                transformOrigin: "center center",
                // Keep pointer-events off on the inner wrapper too so the
                // shadow/subtitle never eat clicks meant for CTAs behind.
                pointerEvents: "none",
              }}
            >
              <img
                src={cfg.hero.center_image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${cfg.hero.center_image_url}` : cfg.hero.center_image_url}
                alt="SUPERMATIK mascote"
                className="object-contain"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                width={cfg.hero?.center_image_width || 480}
                height={cfg.hero?.center_image_width || 480}
                style={{
                  width: "100%",
                  height: "auto",
                  filter: `drop-shadow(0 20px 40px ${cfg.hero?.center_image_glow_color || "#FFE500"}${Math.round((cfg.hero?.center_image_glow_intensity ?? 45) * 2.55).toString(16).padStart(2, "0")})`,
                  opacity: (cfg.hero?.center_image_opacity ?? 100) / 100,
                }}
              />
              {(cfg.hero?.center_subtitle_pt || cfg.hero?.center_subtitle_en || cfg.hero?.center_subtitle_es) && (
                <div
                  className="mt-2 font-[Fredoka] font-bold uppercase tracking-[0.15em] text-center whitespace-nowrap"
                  style={{ color: "#FFFFFF", fontSize: cfg.hero?.center_image_subtitle_size || 18, textShadow: "0 1px 0 rgba(0,0,0,0.55)" }}
                  data-testid="hero-center-subtitle"
                >
                  {pick(cfg.hero, "center_subtitle", lang)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: side image (girl with tablet) — can overlap the header.
         *  z-index is clamped to a minimum of 40 so the image is guaranteed
         *  to render ABOVE the sticky header (which is z-30). This prevents
         *  presets/legacy configs with a lower z from breaking the layering.
         *  `pointer-events: none` is CRITICAL — otherwise this container
         *  would swallow clicks on the language pill / SAIR / other header
         *  actions that sit visually behind the image. The image is purely
         *  decorative, so blocking clicks is safe. */}
        <div
          className="lg:col-span-4 relative h-full min-h-[340px] lg:min-h-[640px] flex items-end justify-center overflow-visible pointer-events-none"
          style={{ position: "relative", zIndex: Math.max(40, (sideVideoActiveUrl && !sideVideoFailed && sideVideoCustomGeom ? cfg.hero?.side_video_z : cfg.hero?.side_image_z) ?? 40) }}
          data-testid="hero-side"
        >
          {(() => {
            const sideVideoUrl = sideVideoActiveUrl;
            const useSideVideo = !!sideVideoUrl && !sideVideoFailed;
            if (!cfg.hero?.side_image_url && !useSideVideo) return null;
            const sideImageSrc = cfg.hero?.side_image_url
              ? (cfg.hero.side_image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${cfg.hero.side_image_url}` : cfg.hero.side_image_url)
              : "";
            const sideVideoSrc = sideVideoUrl.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${sideVideoUrl}` : sideVideoUrl;
            const sideVideoFit = cfg.hero?.side_video_fit || "contain";
            const sideVideoTf = `translate(${cfg.hero?.side_video_pan_x || 0}px, ${cfg.hero?.side_video_pan_y || 0}px) scale(${Math.max(0.25, Math.min(3, (Number(cfg.hero?.side_video_zoom) || 100) / 100))})`;
            const sideVideoSpeed = Math.max(0.25, Math.min(2, Number(cfg.hero?.side_video_speed) || 1));
            // By default the video inherits the image's position/size/effects
            // (fills the same area); "custom" geometry switches to side_video_*.
            const sideP = useSideVideo && sideVideoCustomGeom ? "side_video" : "side_image";
            const aspectSetting = cfg.hero?.side_video_aspect || "image";
            const sideVideoAspectCss = aspectSetting === "auto"
              ? undefined
              : aspectSetting === "image" ? (sideImgRatio || undefined) : aspectSetting.replace("/", " / ");
            const sv = (k, d) => (cfg.hero?.[`${sideP}_${k}`] ?? d);
            const glowColor = sv("glow_color") || "#3DA5FF";
            const intensity = Math.max(0, Math.min(100, sv("glow_intensity", 55)));
            const t = intensity / 100;
            // Multi-layer glow that ACTUALLY scales with intensity:
            //   - Layer 1: tight bloom (blur 8→40px, alpha 25→100%)
            //   - Layer 2: mid halo (blur 20→90px, alpha 15→75%)
            //   - Layer 3: soft outer aura (blur 40→160px, alpha 8→45%)
            // At 100% the total spread reaches ~200px, giving a very visible halo.
            const alphaHex = (a) => Math.round(a * 255).toString(16).padStart(2, "0");
            const glowBase   = intensity === 0 ? "none" : (
              `drop-shadow(0 0 ${Math.round( 8 + 32 * t)}px ${glowColor}${alphaHex(0.25 + 0.75 * t)}) ` +
              `drop-shadow(0 0 ${Math.round(20 + 70 * t)}px ${glowColor}${alphaHex(0.15 + 0.60 * t)}) ` +
              `drop-shadow(0 0 ${Math.round(40 +120 * t)}px ${glowColor}${alphaHex(0.08 + 0.37 * t)})`
            );
            const glowStrong = intensity === 0 ? "none" : (
              `drop-shadow(0 0 ${Math.round(14 + 46 * t)}px ${glowColor}${alphaHex(0.40 + 0.60 * t)}) ` +
              `drop-shadow(0 0 ${Math.round(30 + 90 * t)}px ${glowColor}${alphaHex(0.25 + 0.55 * t)}) ` +
              `drop-shadow(0 0 ${Math.round(60 +160 * t)}px ${glowColor}${alphaHex(0.15 + 0.35 * t)})`
            );
            const effect = sv("effect") || "glow";
            const fxClass = effect && effect !== "none" && effect !== "glow" ? `sm-side-fx-${effect.replace(/_/g, "-")}` : "";
            const baseTf = `translate(${sv("offset_x") || 0}px, ${sv("offset_y") || 0}px) scale(${sv("scale", 1)}) rotate(${sv("rotate") || 0}deg)`;
            // ----- Tablet overlay (per language) --------------------------
            // When admin uploads an MP4 for the active language, it plays
            // silently in loop inside a configurable rectangle on top of the
            // side image. When no MP4 is configured, we fall back to an
            // iframe that runs the actual in-site DEMO ("Aprender a Jogar")
            // on repeat — so the tablet shows exactly what the demo looks
            // like today, no manual video capture required.
            const tabletUrl =
              cfg.hero?.[`tablet_video_url_${lang}`] ||
              cfg.hero?.tablet_video_url_pt ||
              "";
            const enabledFlag = cfg.hero?.tablet_enabled !== false && !useSideVideo;
            const useVideo = enabledFlag && !!tabletUrl;
            const useDemoIframe = enabledFlag && !tabletUrl && !!demoGameId;
            const tabletX = cfg.hero?.tablet_x_pct ?? 56;   // % of wrapper width
            const tabletY = cfg.hero?.tablet_y_pct ?? 48;   // % of wrapper height
            const tabletW = cfg.hero?.tablet_w_pct ?? 26;
            const tabletH = cfg.hero?.tablet_h_pct ?? 20;
            const tabletRotate = cfg.hero?.tablet_rotate ?? 0;
            const tabletRadius = cfg.hero?.tablet_radius ?? 6;
            const tabletBezel = cfg.hero?.tablet_bezel ?? 0;
            const tabletBezelColor = cfg.hero?.tablet_bezel_color || "#1a1a1a";
            const tabletShadow = cfg.hero?.tablet_shadow ?? 0;
            const tabletGlare = cfg.hero?.tablet_glare ?? 0;
            const tabletNotch = !!cfg.hero?.tablet_notch;
            const tabletFit = cfg.hero?.tablet_fit || "auto"; // auto | stretch
            const tabletCornerOffsets = {
              tl: { dx: cfg.hero?.tablet_c_tl_dx || 0, dy: cfg.hero?.tablet_c_tl_dy || 0 },
              tr: { dx: cfg.hero?.tablet_c_tr_dx || 0, dy: cfg.hero?.tablet_c_tr_dy || 0 },
              br: { dx: cfg.hero?.tablet_c_br_dx || 0, dy: cfg.hero?.tablet_c_br_dy || 0 },
              bl: { dx: cfg.hero?.tablet_c_bl_dx || 0, dy: cfg.hero?.tablet_c_bl_dy || 0 },
            };
            const tabletSrc = tabletUrl.startsWith("/")
              ? `${process.env.REACT_APP_BACKEND_URL}${tabletUrl}`
              : tabletUrl;
            const demoIframeSrc = demoGameId
              ? `/jogar/${demoGameId}?mode=training&demo=1&embed=1&lang=${encodeURIComponent(lang || "pt")}`
              : "";
            return (
              <div
                className="relative"
                style={{
                  width: "100%",
                  maxWidth: sv("width") || 680,
                  transform: baseTf,
                  transformOrigin: "bottom center",
                  opacity: (sv("opacity", 100)) / 100,
                  alignSelf: useSideVideo && sideVideoCustomGeom
                    ? ({ top: "flex-start", center: "center", bottom: "flex-end" }[cfg.hero?.side_video_valign || "top"])
                    : undefined,
                }}
              >
                {useSideVideo ? (
                  <AlphaVideo
                    key={sideVideoSrc}
                    src={sideVideoSrc}
                    poster={sideImageSrc || undefined}
                    loop={cfg.hero?.side_video_loop !== false}
                    playbackRate={sideVideoSpeed}
                    onError={() => setSideVideoFailed(true)}
                    className={`object-bottom ${fxClass}`}
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      objectFit: sideVideoFit,
                      aspectRatio: sideVideoAspectCss,
                      background: "transparent",
                      borderRadius: cfg.hero?.side_video_radius ?? 16,
                      border: (cfg.hero?.side_video_border || 0) > 0 ? `${cfg.hero.side_video_border}px solid ${cfg.hero?.side_video_border_color || "#3DA5FF"}` : undefined,
                      filter: glowBase,
                      transform: sideVideoTf,
                      transformOrigin: "center center",
                      "--sm-side-tf": sideVideoTf,
                      "--sm-glow-base": glowBase,
                      "--sm-glow-strong": glowStrong,
                    }}
                    testid="hero-side-video"
                  />
                ) : (
                <img
                  src={sideImageSrc}
                  alt="aluna com tablet"
                  className={`object-contain object-bottom ${fxClass}`}
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  width={cfg.hero?.side_image_width || 680}
                  height={Math.round((cfg.hero?.side_image_width || 680) * 1.2)}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    filter: glowBase,
                    "--sm-side-tf": "none",
                    "--sm-glow-base": glowBase,
                    "--sm-glow-strong": glowStrong,
                  }}
                  data-testid="hero-side-image"
                />
                )}
                {useVideo && (
                  <TabletDemoFrame
                    videoSrc={tabletSrc}
                    xPct={tabletX}
                    yPct={tabletY}
                    wPct={tabletW}
                    hPct={tabletH}
                    rotate={tabletRotate}
                    radius={tabletRadius}
                    bezel={tabletBezel}
                    bezelColor={tabletBezelColor}
                    shadow={tabletShadow}
                    glare={tabletGlare}
                    notch={tabletNotch}
                    fit={tabletFit}
                    posterUrl={cfg.hero?.tablet_poster_url || ""}
                    glow={cfg.hero?.tablet_glow ?? 0}
                    glowColor={cfg.hero?.tablet_glow_color || "#3DA5FF"}
                    glowPulse={!!cfg.hero?.tablet_glow_pulse}
                    innerShadow={cfg.hero?.tablet_inner_shadow ?? 0}
                    scanlines={cfg.hero?.tablet_scanlines ?? 0}
                    tilt={cfg.hero?.tablet_tilt ?? 0}
                    tiltAxis={cfg.hero?.tablet_tilt_axis || "x"}
                    cornerOffsets={tabletCornerOffsets}
                    fxNeonRing={!!cfg.hero?.tablet_fx_neon_ring}
                    fxRainbow={!!cfg.hero?.tablet_fx_rainbow}
                    fxSparkles={!!cfg.hero?.tablet_fx_sparkles}
                    fxThickness={cfg.hero?.tablet_fx_thickness ?? 20}
                    demoAlign={cfg.hero?.tablet_demo_align || "right"}
                    langKey={lang}
                  />
                )}
                {useDemoIframe && (
                  <TabletDemoFrame
                    src={demoIframeSrc}
                    xPct={tabletX}
                    yPct={tabletY}
                    wPct={tabletW}
                    hPct={tabletH}
                    rotate={tabletRotate}
                    radius={tabletRadius}
                    bezel={tabletBezel}
                    bezelColor={tabletBezelColor}
                    shadow={tabletShadow}
                    glare={tabletGlare}
                    notch={tabletNotch}
                    fit={tabletFit}
                    posterUrl={cfg.hero?.tablet_poster_url || ""}
                    glow={cfg.hero?.tablet_glow ?? 0}
                    glowColor={cfg.hero?.tablet_glow_color || "#3DA5FF"}
                    glowPulse={!!cfg.hero?.tablet_glow_pulse}
                    innerShadow={cfg.hero?.tablet_inner_shadow ?? 0}
                    scanlines={cfg.hero?.tablet_scanlines ?? 0}
                    tilt={cfg.hero?.tablet_tilt ?? 0}
                    tiltAxis={cfg.hero?.tablet_tilt_axis || "x"}
                    cornerOffsets={tabletCornerOffsets}
                    fxNeonRing={!!cfg.hero?.tablet_fx_neon_ring}
                    fxRainbow={!!cfg.hero?.tablet_fx_rainbow}
                    fxSparkles={!!cfg.hero?.tablet_fx_sparkles}
                    fxThickness={cfg.hero?.tablet_fx_thickness ?? 20}
                    demoAlign={cfg.hero?.tablet_demo_align || "right"}
                    langKey={lang}
                  />
                )}
              </div>
            );
          })()}
        </div>
        {/* "Loja online" overlay: sibling of the z-40 side column so it stacks
            ABOVE the JOGA ONLINE block (z-50); follows the video box live. */}
        {storeOverlayOn && videoRect && (
          <SideVideoStoreButton h={cfg.hero || {}} lang={lang} variant="desktop" rect={videoRect} />
        )}
      </div>
    </section>
  );
});


/* ============================================================================
   PaintedTitleBanner — recreates the reference's hand-painted blue brush-
   stroke banner with yellow paint splatters on the sides and 4 yellow sparkle
   stars. Fully configurable via `paintColor`, `splatterColor`, `showSplatter`.
   ============================================================================ */
function PaintedTitleBanner({ title, paintColor = "#1B6FD1", splatterColor = "#FFD93D", showSplatter = true, uid = "banner", width, height, offsetX = 0, offsetY = 0, scale = 1, roughness = 10, paddingX = 40, paddingY = 12 }) {
  const gid = `paint-${uid}`;
  const fid = `rough-${uid}`;
  const wrapStyle = {
    paddingLeft: paddingX,
    paddingRight: paddingX,
    paddingTop: paddingY,
    paddingBottom: paddingY,
    transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
    transformOrigin: "center center",
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
  };
  return (
    <div className="relative inline-block" style={wrapStyle} data-testid="phases-painted-title">
      {/* Yellow splatter marks — left + right edges (behind the blue blob) */}
      {showSplatter && (
        <svg viewBox="0 0 700 160" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden style={{ overflow: "visible" }}>
          <defs>
            <filter id={`splat-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
              <feTurbulence type="fractalNoise" baseFrequency="0.6" numOctaves="2" seed="3" />
              <feDisplacementMap in="SourceGraphic" scale="12" />
            </filter>
          </defs>
          {/* Left splatter cluster */}
          <g fill={splatterColor} filter={`url(#splat-${uid})`} opacity="0.95">
            <ellipse cx="30"  cy="70"  rx="42" ry="22" />
            <ellipse cx="12"  cy="120" rx="28" ry="14" />
            <ellipse cx="55"  cy="130" rx="22" ry="10" />
            <ellipse cx="0"   cy="50"  rx="18" ry="10" />
            <circle  cx="-8"  cy="90"  r="6" />
            <circle  cx="70"  cy="30"  r="5" />
          </g>
          {/* Right splatter cluster */}
          <g fill={splatterColor} filter={`url(#splat-${uid})`} opacity="0.95">
            <ellipse cx="670" cy="70"  rx="48" ry="24" />
            <ellipse cx="688" cy="120" rx="30" ry="14" />
            <ellipse cx="640" cy="130" rx="22" ry="10" />
            <ellipse cx="708" cy="50"  rx="18" ry="10" />
            <circle  cx="720" cy="90"  r="7" />
            <circle  cx="628" cy="30"  r="5" />
          </g>
        </svg>
      )}

      {/* Blue rectangular brush-stroke blob */}
      <svg viewBox="0 0 600 130" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={shadeHex(paintColor, 1.35)} />
            <stop offset="55%" stopColor={paintColor} />
            <stop offset="100%" stopColor={shadeHex(paintColor, 0.7)} />
          </linearGradient>
          <filter id={fid} x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence type="fractalNoise" baseFrequency="0.05" numOctaves="2" seed="7" />
            <feDisplacementMap in="SourceGraphic" scale={roughness} />
          </filter>
        </defs>
        {/* Rectangular blob (more square than rounded) with rough edges */}
        <path
          d="M40,20 L560,15 C580,15 590,25 590,45 L590,95 C590,115 575,120 555,120 L45,118 C22,118 12,110 12,90 L15,42 C15,25 25,20 40,20 Z"
          fill={`url(#${gid})`}
          filter={`url(#${fid})`}
        />
        {/* Specular highlight strip */}
        <path
          d="M40,42 L555,38 C570,38 570,55 555,58 L45,60 C30,60 30,42 40,42 Z"
          fill="rgba(255,255,255,0.28)"
          filter={`url(#${fid})`}
        />
      </svg>

      <h2
        className="relative font-[Fredoka] font-black uppercase tracking-wide whitespace-nowrap text-2xl sm:text-3xl md:text-4xl"
        style={{ color: "#FFFFFF", textShadow: "0 3px 0 rgba(0,0,0,0.55), 0 0 12px rgba(0,0,0,0.4)" }}
      >
        {title}
      </h2>

      {/* 4 yellow sparkle stars decorating the banner */}
      {[
        { x: "-6%",  y: "-30%", s: 16 },
        { x: "12%",  y: "115%", s: 12 },
        { x: "84%",  y: "-25%", s: 14 },
        { x: "105%", y: "70%",  s: 18 },
      ].map((sp) => (
        <span
          key={`splatter-${sp.x}-${sp.y}`}
          className="absolute pointer-events-none"
          style={{
            left: sp.x, top: sp.y, width: sp.s, height: sp.s,
            background: splatterColor,
            clipPath: "polygon(50% 0,60% 40%,100% 50%,60% 60%,50% 100%,40% 60%,0 50%,40% 40%)",
            filter: `drop-shadow(0 0 6px ${splatterColor})`,
          }}
          aria-hidden
        />
      ))}
    </div>
  );
}

/* Local hex shader (module-scope, works without CtaRow shade closure) */
function shadeHex(hex, factor) {
  const h = (hex || "#000000").replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  let r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  r = Math.max(0, Math.min(255, Math.round(r * factor)));
  g = Math.max(0, Math.min(255, Math.round(g * factor)));
  b = Math.max(0, Math.min(255, Math.round(b * factor)));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/* ============================================================================
   FannedCards — 3 stylized number cards (3 / 5 / 7) fanned out like the
   illustration in the reference image. Used for Phase 1 ("Treinos Livres").
   ============================================================================ */
function FannedCards() {
  const cards = [
    { n: 3, color: "#1FAE57", tilt: -22, x: -28 },
    { n: 5, color: "#2AAFA0", tilt:   0, x:   0 },
    { n: 7, color: "#1FAE57", tilt:  22, x:  28 },
  ];
  return (
    <div className="relative" style={{ width: 120, height: 100 }} aria-hidden>
      {cards.map((c, i) => (
        <div
          key={`card-${c.color}-${i}`}
          className="absolute top-0 left-1/2 grid place-items-center font-[Fredoka] font-black text-white"
          style={{
            width: 38,
            height: 56,
            marginLeft: -19,
            background: `linear-gradient(180deg, ${c.color} 0%, ${c.color} 50%, rgba(0,0,0,0.25) 100%)`,
            transform: `translateX(${c.x}px) translateY(${i === 1 ? -4 : 8}px) rotate(${c.tilt}deg)`,
            borderRadius: 6,
            boxShadow: "0 6px 12px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.3)",
            fontSize: 24,
            border: "2px solid rgba(255,255,255,0.85)",
          }}
        >
          {c.n}
        </div>
      ))}
    </div>
  );
}

/* ============================================================================
   TealArrow — solid right-pointing arrow with cyan glow (replaces the
   dashed arrows in the previous implementation).
   ============================================================================ */
function TealArrow() {
  return (
    <svg
      viewBox="0 0 40 24"
      className="w-7 h-5 sm:w-9 sm:h-6"
      aria-hidden
      style={{ filter: "drop-shadow(0 0 6px #4ECDC4)" }}
    >
      <path d="M2 12 L26 12 M20 4 L30 12 L20 20" stroke="#4ECDC4" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


/* =========================================================================
   PHASES + ONLINE — side-by-side blocks (left: horizontal phases row with
   numbered circles + iconographic ovals + dotted arrows between; right:
   3 Online mode cards)
   ========================================================================= */
function PhasesAndOnline({ cfg, lang, gfConfig }) {
  const phases = cfg.phases_section?.phases || [];
  const cards = cfg.online_section?.cards || [];

  // Track mobile viewport so we can render the "APRENDER A JOGAR" secondary
  // block EITHER inside the online-block (desktop, for the tight visual bond
  // with the VS TEMPO/ROBOT/HUMANO cards) OR as a separate sibling of the grid
  // AFTER phases-block (mobile, per user reference layout). Rendering only
  // once avoids double-loading the YouTube iframe.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023.98px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Maps phase id → big iconography for the oval bubble below the title.
  // Phase 2 is the only one that renders TWO icons split by "ou" — matches
  // the original SUPERTMATIK marketing layout (school + online laptop).
  const PHASE_ICONS = {
    treinos: { color: "emerald", icons: [Spade] },
    rumo: { color: "orange", icons: [School, Laptop], splitLabel: { pt: "ou", en: "or", es: "o" } },
    final: { color: "sky", icons: [Trophy] },
  };

  const PHASE_BUBBLE = {
    emerald: "bg-emerald-100/70 text-emerald-700",
    orange: "bg-orange-100/70 text-orange-600",
    sky: "bg-sky-100/70 text-blue-600",
  };

  return (
    <section className="relative overflow-visible" data-testid="phases-online-section">
      <div className="max-w-7xl mx-auto px-6 pt-2 pb-2 grid lg:grid-cols-5 gap-6 items-start">
        {/* PHASES — premium dark widescreen card with overlapping painted banner */}
        <div
          className="lg:col-span-3 relative"
          style={{
            marginTop: (cfg.phases_section?.margin_top ?? 28) + (cfg.phases_section?.offset_y || 0),
            transform: `translateX(${cfg.phases_section?.offset_x || 0}px)`,
            maxWidth: cfg.phases_section?.container_width && cfg.phases_section.container_width > 0 ? cfg.phases_section.container_width : undefined,
            zIndex: cfg.phases_section?.z_index ?? 50,
          }}
          data-testid="phases-block"
        >
          {/* Overlapping painted brush-stroke banner — sits ABOVE the container */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10" style={{ minWidth: 260 }}>
            <PaintedTitleBanner
              title={pick(cfg.phases_section, "title", lang)}
              paintColor={cfg.phases_section?.banner_paint_color || "#1B6FD1"}
              splatterColor={cfg.phases_section?.banner_splatter_color || "#FFD93D"}
              showSplatter={cfg.phases_section?.banner_show_splatter !== false}
              width={cfg.phases_section?.banner_width || undefined}
              height={cfg.phases_section?.banner_height || undefined}
              offsetX={cfg.phases_section?.banner_offset_x || 0}
              offsetY={cfg.phases_section?.banner_offset_y || 0}
              scale={cfg.phases_section?.banner_scale ?? 1}
              roughness={cfg.phases_section?.banner_roughness ?? 10}
              paddingX={cfg.phases_section?.banner_padding_x ?? 40}
              paddingY={cfg.phases_section?.banner_padding_y ?? 12}
              uid="phases"
            />
          </div>

          {/* Main container — gradient navy + neon blue border + outer glow */}
          <div
            className={`relative pt-12 pb-6 px-7 ${cfg.phases_section?.effect_shimmer ? "sm-shimmer" : ""} ${cfg.phases_section?.effect_pulse_glow ? "sm-pulse-glow" : ""}`}
            style={{
              background: cfg.phases_section?.container_bg
                ? `linear-gradient(180deg, ${cfg.phases_section.container_bg} 0%, ${cfg.phases_section.container_bg} 100%)`
                : "linear-gradient(180deg, #020B2D 0%, #071B5A 100%)",
              border: `2px solid ${cfg.phases_section?.container_border || "#3DA5FF"}`,
              borderRadius: cfg.phases_section?.container_radius || 24,
              height: cfg.phases_section?.container_height && cfg.phases_section.container_height > 0 ? cfg.phases_section.container_height : undefined,
              boxShadow: `0 0 24px ${cfg.phases_section?.container_glow_color || "#3DA5FF"}${Math.round((cfg.phases_section?.container_glow_intensity ?? 40) * 2.55).toString(16).padStart(2, "0")}, 0 0 48px ${cfg.phases_section?.container_glow_color || "#3DA5FF"}22, 0 20px 60px -20px rgba(0,0,0,0.7)`,
              "--sm-neon": cfg.phases_section?.container_glow_color || "#3DA5FF",
            }}
          >
            {/* Desktop 3-column layout (numbers row, icons row, descriptions row)
                — hidden on mobile via CSS. Mobile uses a vertical per-phase stack
                rendered below. */}
            <div data-testid="phases-desktop-layout">
            {/* ── Phase numbers row (1/2/3 in colored circles + labels) ── */}
            <div className="grid grid-cols-3" style={{ columnGap: cfg.phases_section?.column_gap ?? 30, marginBottom: cfg.phases_section?.gap_title_to_icon ?? cfg.phases_section?.inner_row_gap ?? 16 }}>
              {phases.map((p, idx) => {
                const numColors = ["#39E635", "#FFD93D", "#3DA5FF"];
                const numColor = p.num_color || numColors[idx] || "#3DA5FF";
                const titleColor = p.title_color || "#FFFFFF";
                const nSize = cfg.phases_section?.num_circle_size ?? 44;
                return (
                  <div key={`num-${p.id}`} className="flex flex-col items-center text-center" data-testid={`phase-num-${p.id}`}>
                    <div
                      className={`rounded-full grid place-items-center font-[Fredoka] font-black ${cfg.phases_section?.effect_bounce_numbers ? "sm-bounce-numbers" : ""}`}
                      style={{
                        width: nSize,
                        height: nSize,
                        fontSize: nSize * 0.5,
                        background: numColor,
                        color: numColor === "#FFD93D" ? "#0A0E2A" : "#FFFFFF",
                        boxShadow: `0 0 22px ${numColor}AA, inset 0 -2px 0 rgba(0,0,0,0.18)`,
                        marginBottom: cfg.phases_section?.gap_num_to_title ?? 8,
                        transform: `translate(${p.num_offset_x || 0}px, ${p.num_offset_y || 0}px)`,
                      }}
                    >
                      {p.number}
                    </div>
                    <h3
                      className="font-[Fredoka] font-black uppercase tracking-wide leading-tight"
                      style={{
                        color: titleColor,
                        fontSize: cfg.phases_section?.phase_title_size ?? 18,
                        textShadow: "0 1px 0 rgba(0,0,0,0.5)",
                        transform: `translate(${p.title_offset_x || 0}px, ${p.title_offset_y || 0}px)`,
                      }}
                    >
                      {pick(p, "title", lang)}
                    </h3>
                  </div>
                );
              })}
            </div>

            {/* ── 3D Illustrations row with cyan arrows between phases ── */}
            {(() => {
              const globalSize = cfg.phases_section?.phase_icon_size ?? 64;
              const sizeFor = (i) => (phases[i]?.icon_size && phases[i].icon_size > 0) ? phases[i].icon_size : globalSize;
              const s0 = sizeFor(0), s1 = sizeFor(1), s2 = sizeFor(2);
              const rowMinH = Math.max(s0, s1, s2 + 8) + 30;
              return (
                <div
                  className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center"
                  style={{ marginBottom: cfg.phases_section?.gap_icon_to_desc ?? cfg.phases_section?.inner_row_gap ?? 16, columnGap: Math.round((cfg.phases_section?.column_gap ?? 30) / 3) }}
                >
                  {/* Slot 1 — phase 0 icon */}
                  <div className="flex items-center justify-center" style={{ minHeight: rowMinH }}>
                    <PhaseIcon
                      phase={phases[0]}
                      size={s0}
                      fallbackNode={<FannedCards />}
                      glow="rgba(57,230,53,0.55)"
                    />
                  </div>
                  <TealArrow />
                  {/* Slot 2 — phase 1 icon (single icon, matching Fase 1 e 3) */}
                  <div className="flex items-center justify-center" style={{ minHeight: rowMinH }}>
                    <PhaseIcon
                      phase={phases[1]}
                      size={s1}
                      defaultEmoji="🏫"
                      glow="rgba(255,107,0,0.55)"
                    />
                  </div>
                  <TealArrow />
                  {/* Slot 3 — phase 2 icon */}
                  <div className="flex items-center justify-center" style={{ minHeight: rowMinH }}>
                    <PhaseIcon
                      phase={phases[2]}
                      size={s2 + 8}
                      defaultEmoji="🏆"
                      glow="rgba(255,215,0,0.6)"
                    />
                  </div>
                </div>
              );
            })()}

            {/* ── Descriptions row ── */}
            <div className="grid grid-cols-3" style={{ columnGap: cfg.phases_section?.column_gap ?? 30 }}>
              {phases.map((p) => (
                <p
                  key={`desc-${p.id}`}
                  className="text-center mx-auto"
                  style={{
                    color: "rgba(255,255,255,0.88)",
                    fontSize: cfg.phases_section?.phase_desc_size ?? 13,
                    lineHeight: cfg.phases_section?.phase_desc_line_height ?? 1.625,
                    maxWidth: 220,
                  }}
                >
                  {pick(p, "description", lang)}
                </p>
              ))}
            </div>
            </div>{/* /phases-desktop-layout */}

            {/* Mobile vertical stack — each phase rendered as a full-width
                column (number, title, icon, description). Only visible on
                mobile via CSS. Follows the reference screenshot layout. */}
            <div data-testid="phases-mobile-layout" style={{ display: "none" }}>
              {phases.map((p, idx) => {
                const numColors = ["#39E635", "#FFD93D", "#3DA5FF"];
                const numColor = p.num_color || numColors[idx] || "#3DA5FF";
                const titleColor = p.title_color || "#FFFFFF";
                const nSize = cfg.phases_section?.num_circle_size ?? 44;
                const globalSize = cfg.phases_section?.phase_icon_size ?? 64;
                // On mobile we render a big vertical icon (fallback = 180px)
                // so each phase image is a proper illustration slot.
                const iconSize = (p.icon_size && p.icon_size > 0)
                  ? Math.max(120, p.icon_size)
                  : Math.max(160, globalSize * 2.5);
                const glows = ["rgba(57,230,53,0.55)", "rgba(255,107,0,0.55)", "rgba(255,215,0,0.6)"];
                const emojis = ["🎯", "🏫", "🏆"];
                const fallbackNode = idx === 0 ? <FannedCards /> : null;
                return (
                  <div
                    key={`mstack-${p.id}`}
                    className="flex flex-col items-center text-center"
                    style={{ marginBottom: idx < phases.length - 1 ? 28 : 8 }}
                    data-testid={`phase-mobile-${p.id}`}
                  >
                    {/* Number circle */}
                    <div
                      className={`rounded-full grid place-items-center font-[Fredoka] font-black ${cfg.phases_section?.effect_bounce_numbers ? "sm-bounce-numbers" : ""}`}
                      style={{
                        width: nSize,
                        height: nSize,
                        fontSize: nSize * 0.5,
                        background: numColor,
                        color: numColor === "#FFD93D" ? "#0A0E2A" : "#FFFFFF",
                        boxShadow: `0 0 22px ${numColor}AA, inset 0 -2px 0 rgba(0,0,0,0.18)`,
                        marginBottom: 8,
                      }}
                    >
                      {p.number}
                    </div>
                    {/* Title */}
                    <h3
                      className="font-[Fredoka] font-black uppercase tracking-wide leading-tight"
                      style={{
                        color: titleColor,
                        fontSize: cfg.phases_section?.phase_title_size_mobile || 15,
                        textShadow: "0 1px 0 rgba(0,0,0,0.5)",
                        marginBottom: 12,
                        maxWidth: "90%",
                      }}
                    >
                      {pick(p, "title", lang)}
                    </h3>
                    {/* Icon / illustration */}
                    <div className="flex items-center justify-center" style={{ marginBottom: 12, minHeight: iconSize }}>
                      <PhaseIcon
                        phase={p}
                        size={iconSize}
                        fallbackNode={fallbackNode}
                        defaultEmoji={emojis[idx]}
                        glow={glows[idx] || glows[2]}
                      />
                    </div>
                    {/* Description */}
                    <p
                      style={{
                        color: "rgba(255,255,255,0.88)",
                        fontSize: cfg.phases_section?.phase_desc_size_mobile || 12,
                        lineHeight: cfg.phases_section?.phase_desc_line_height ?? 1.625,
                        maxWidth: 280,
                      }}
                    >
                      {pick(p, "description", lang)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* ONLINE CARDS — premium widescreen card with overlapping painted banner */}
        <div
          className="lg:col-span-2 relative"
          style={{
            marginTop: (cfg.online_section?.margin_top ?? 28) + (cfg.online_section?.offset_y || 0),
            transform: `translateX(${cfg.online_section?.offset_x || 0}px)`,
            maxWidth: cfg.online_section?.container_width || undefined,
            zIndex: cfg.online_section?.z_index ?? 50,
          }}
          data-testid="online-block"
        >
          {/* Overlapping painted brush-stroke banner — JOGA ONLINE */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10" style={{ minWidth: 240 }}>
            <PaintedTitleBanner
              title={pick(cfg.online_section, "title", lang)}
              paintColor={cfg.online_section?.banner_paint_color || "#1B6FD1"}
              splatterColor={cfg.online_section?.banner_splatter_color || "#FFD93D"}
              showSplatter={cfg.online_section?.banner_show_splatter !== false}
              width={cfg.online_section?.banner_width || undefined}
              height={cfg.online_section?.banner_height || undefined}
              offsetX={cfg.online_section?.banner_offset_x || 0}
              offsetY={cfg.online_section?.banner_offset_y || 0}
              scale={cfg.online_section?.banner_scale ?? 1}
              roughness={cfg.online_section?.banner_roughness ?? 10}
              paddingX={cfg.online_section?.banner_padding_x ?? 40}
              paddingY={cfg.online_section?.banner_padding_y ?? 12}
              uid="online"
            />
          </div>

          {/* Main container — gradient navy + neon blue border + outer glow */}
          <div
            className={`relative pb-6 px-5 overflow-hidden ${cfg.online_section?.effect_pulse_glow ? "sm-pulse-glow" : ""}`}
            style={{
              background: cfg.online_section?.container_bg
                ? `linear-gradient(180deg, ${cfg.online_section.container_bg} 0%, ${cfg.online_section.container_bg} 100%)`
                : "linear-gradient(180deg, #030B2A 0%, #07194D 100%)",
              border: `2px solid ${cfg.online_section?.container_border || "#3DA5FF"}`,
              borderRadius: cfg.online_section?.container_radius || 28,
              // Use `minHeight` so the admin's height setting acts as a floor —
              // the container must grow when the GRANDE FINAL card is rendered
              // beneath the 3 VS cards (otherwise `overflow-hidden` clips it).
              minHeight: cfg.online_section?.container_height && cfg.online_section.container_height > 0 ? cfg.online_section.container_height : undefined,
              paddingTop: cfg.online_section?.cards_offset_top ?? 56,
              boxShadow: `0 0 24px ${cfg.online_section?.container_glow_color || "#3DA5FF"}${Math.round((cfg.online_section?.container_glow_intensity ?? 40) * 2.55).toString(16).padStart(2, "0")}, 0 0 48px ${cfg.online_section?.container_glow_color || "#3DA5FF"}22, 0 20px 60px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)`,
              "--sm-neon": cfg.online_section?.container_glow_color || "#3DA5FF",
              contain: "layout paint",
            }}
          >
            <div className="grid grid-cols-3" style={{ gap: cfg.online_section?.card_gap || 14 }}>
              {cards.map((c, idx) => {
                // Map neon color + lucide icon per card position
                const palette = [
                  { neon: "#3DA5FF", deep: "#0B1F4A", icon: Timer },  // VS TEMPO
                  { neon: "#39E635", deep: "#0A2E12", icon: Bot   },  // VS ROBOT
                  { neon: "#FF1A8C", deep: "#3A0C28", icon: Users },  // VS HUMANO
                ];
                const P = palette[idx] || palette[0];
                const Icon = P.icon;
                return (
                  <div
                    key={c.id}
                    className={`relative overflow-hidden flex flex-col items-center text-center ${cfg.online_section?.effect_shimmer ? "sm-shimmer" : ""} ${cfg.online_section?.effect_pulse_glow ? "sm-pulse-glow" : ""}`}
                    style={{
                      borderRadius: cfg.online_section?.card_radius || 24,
                      padding: "22px 14px",
                      minHeight: cfg.online_section?.card_height && cfg.online_section.card_height > 0 ? cfg.online_section.card_height : undefined,
                      background: `linear-gradient(180deg, ${P.deep} 0%, rgba(13,18,55,0.85) 55%, ${P.neon}22 100%)`,
                      border: `2px solid ${P.neon}`,
                      boxShadow: `0 0 22px ${P.neon}66, inset 0 0 26px ${P.neon}22, 0 6px 14px -4px rgba(0,0,0,0.5)`,
                      "--sm-neon": P.neon,
                      contain: "layout paint",
                    }}
                    data-testid={`online-card-${c.id}`}
                  >
                {/* DEMO badge — VS TEMPO (first card). Informative by default;
                    becomes a link when `demo_badge_url` is configured in admin. */}
                {idx === 0 && (() => {
                  const demoUrl = c.demo_badge_url || "/demo";
                  const badgeStyle = {
                    background: "#7C5CFF",
                    color: "#FFFFFF",
                    padding: "3px 8px 4px 7px",
                    borderTopRightRadius: cfg.online_section?.card_radius || 24,
                    borderBottomLeftRadius: 12,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    lineHeight: 1,
                    boxShadow: "0 2px 10px rgba(124,92,255,0.55)",
                    zIndex: 4,
                  };
                  const badgeTitle = demoUrl
                    ? (lang === "en" ? "Open the demo" : lang === "es" ? "Abrir la demostración" : "Abrir a demonstração")
                    : (lang === "en" ? "This mode has a free demo" : lang === "es" ? "Este modo tiene una demostración gratuita" : "Este modo tem uma demonstração gratuita");
                  const baseCls = "absolute top-0 right-0 flex items-center gap-1 select-none";
                  const inner = (<><GraduationCap style={{ width: 13, height: 13 }} aria-hidden /><span className="font-[Fredoka]">DEMO</span></>);
                  if (demoUrl) {
                    const isExternal = /^https?:\/\//i.test(demoUrl);
                    const linkCls = `${baseCls} cursor-pointer transition hover:brightness-110 hover:scale-105`;
                    if (isExternal) {
                      return (
                        <a href={demoUrl} target="_blank" rel="noopener noreferrer" className={linkCls} style={badgeStyle} title={badgeTitle} data-testid="vs-tempo-demo-badge">{inner}</a>
                      );
                    }
                    return (
                      <Link to={demoUrl} className={linkCls} style={badgeStyle} title={badgeTitle} data-testid="vs-tempo-demo-badge">{inner}</Link>
                    );
                  }
                  return (
                    <div className={baseCls} style={badgeStyle} title={badgeTitle} data-testid="vs-tempo-demo-badge">{inner}</div>
                  );
                })()}
                {(() => {
                  const iconUrl = c.icon_url;
                  const emoji = c.emoji_override;
                  const iconSize = cfg.online_section?.card_icon_size ?? 56;
                  const mb = cfg.online_section?.inner_row_gap ?? 16;
                  if (iconUrl) {
                    const src = iconUrl.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${iconUrl}` : iconUrl;
                    return (
                      <img
                        src={src}
                        alt=""
                        className="flex-shrink-0 object-contain"
                        loading="lazy"
                        decoding="async"
                        style={{ width: iconSize, height: iconSize, marginBottom: mb, filter: `drop-shadow(0 0 16px ${P.neon}AA)` }}
                        aria-hidden
                      />
                    );
                  }
                  if (emoji) {
                    return (
                      <span aria-hidden style={{ fontSize: iconSize, lineHeight: 1, marginBottom: mb, filter: `drop-shadow(0 0 16px ${P.neon}AA)` }}>{emoji}</span>
                    );
                  }
                  return (
                    <Icon
                      className="flex-shrink-0"
                      style={{
                        width: iconSize,
                        height: iconSize,
                        color: P.neon,
                        strokeWidth: 2,
                        filter: `drop-shadow(0 0 16px ${P.neon}AA) drop-shadow(0 0 4px ${P.neon})`,
                        marginBottom: mb,
                      }}
                      aria-hidden
                    />
                  );
                })()}
                    {/* Title — uppercase bold in the card's own neon color */}
                    <h3
                      className="font-[Fredoka] font-black uppercase tracking-wide"
                      style={{
                        color: P.neon,
                        fontSize: cfg.online_section?.card_title_size ?? 22,
                        textShadow: `0 0 10px ${P.neon}AA, 0 1px 0 rgba(0,0,0,0.4)`,
                        marginBottom: (cfg.online_section?.inner_row_gap ?? 16) / 2,
                      }}
                    >
                      {pick(c, "title", lang)}
                    </h3>
                    {/* Description */}
                    <p
                      className="leading-snug flex-1"
                      style={{
                        color: "rgba(255,255,255,0.92)",
                        fontSize: cfg.online_section?.card_desc_size ?? 14,
                        maxWidth: 160,
                        marginBottom: cfg.online_section?.inner_row_gap ?? 16,
                      }}
                    >
                      {pick(c, "description", lang)}
                    </p>
                    {/* Pill JOGAR button — configurable height/font */}
                    <Link
                      to={c.url || "/"}
                      className="font-[Fredoka] font-black uppercase tracking-wider transition hover:scale-105 grid place-items-center"
                      style={{
                        width: cfg.online_section?.card_button_w ?? 140,
                        height: cfg.online_section?.card_button_h ?? 42,
                        borderRadius: (cfg.online_section?.card_button_h ?? 42) / 2,
                        background: P.neon,
                        color: "#FFFFFF",
                        fontSize: cfg.online_section?.card_button_size ?? 16,
                        boxShadow: `0 0 18px ${P.neon}, inset 0 -3px 0 rgba(0,0,0,0.18), inset 0 2px 0 rgba(255,255,255,0.35)`,
                        textShadow: "0 1px 0 rgba(0,0,0,0.25)",
                      }}
                      data-testid={`online-card-${c.id}-cta`}
                    >
                      {pick(c, "button", lang)}
                    </Link>
                  </div>
                );
              })}
            </div>

            {/* GRANDE FINAL — 4th card rendered INSIDE the TREINOS
                container, spanning full width below the 3 VS cards
                (VS TEMPO / VS ROBOT / VS HUMANO). Same visual recipe
                (dark gradient + neon border) but amber palette. User
                anexo "para aceder treino grande final" (Feb-2026).
                Hidden when the admin toggle is off. */}
            {gfConfig?.enabled && gfConfig?.name && (() => {
              const P = { neon: "#FBBF24", deep: "#1a1409" };
              const titleSize = cfg.online_section?.card_title_size ?? 22;
              const descSize = cfg.online_section?.card_desc_size ?? 14;
              const btnH = cfg.online_section?.card_button_h ?? 42;
              const btnFont = cfg.online_section?.card_button_size ?? 16;
              const glowOn = gfConfig.glow_enabled !== false;
              const pulseOn = gfConfig.pulse_enabled !== false;
              return (
                <div
                  className={`relative overflow-hidden ${pulseOn && glowOn ? "sm-pulse-glow" : ""}`}
                  style={{
                    marginTop: (cfg.online_section?.card_gap ?? 14) + (gfConfig.offset_y || 0),
                    borderRadius: cfg.online_section?.card_radius || 24,
                    background: `linear-gradient(180deg, ${P.deep} 0%, #0a0a1f 100%)`,
                    border: `2px solid ${P.neon}`,
                    boxShadow: glowOn
                      ? `0 0 22px ${P.neon}99, 0 0 48px ${P.neon}55, inset 0 0 20px ${P.neon}22`
                      : "inset 0 0 10px rgba(0,0,0,0.4)",
                    "--sm-neon": P.neon,
                  }}
                  data-testid="treinos-card-grande-final"
                >
                  <div className="flex items-center" style={{ padding: "18px 20px", gap: 16 }}>
                    {/* Left column: Trophy icon stacked above the title */}
                    <div className="flex flex-col items-center justify-center shrink-0" style={{ minWidth: 140, gap: 6 }}>
                      <Trophy
                        style={{
                          width: 48, height: 48, color: P.neon, strokeWidth: 2.2,
                          filter: `drop-shadow(0 0 10px ${P.neon}AA)`,
                        }}
                        aria-hidden
                      />
                      <div
                        className="font-[Fredoka] font-black uppercase tracking-wide leading-tight text-center"
                        style={{ color: P.neon, fontSize: titleSize, textShadow: `0 0 8px ${P.neon}AA` }}
                      >
                        {gfConfig.name}
                      </div>
                    </div>

                    {/* Right column: subtitle stacked above the JOGAR CTA */}
                    <div className="flex-1 flex flex-col items-center justify-center" style={{ gap: 10 }}>
                      {gfConfig.subtitle && (
                        <p
                          className="leading-snug text-center"
                          style={{ color: "rgba(255,255,255,0.92)", fontSize: descSize }}
                        >
                          {gfConfig.subtitle}
                        </p>
                      )}
                      <Link
                        to="/treino/grande-final"
                        className="font-[Fredoka] font-black uppercase tracking-wider transition hover:scale-105 grid place-items-center"
                        style={{
                          width: (cfg.online_section?.card_button_w ?? 0) > 0 ? (cfg.online_section?.card_button_w ?? 140) : 160,
                          height: btnH,
                          borderRadius: btnH / 2,
                          background: `linear-gradient(180deg, #FFD54A 0%, ${P.neon} 100%)`,
                          color: "#1f2937",
                          fontSize: btnFont,
                          boxShadow: `0 0 14px ${P.neon}, inset 0 -3px 0 rgba(0,0,0,0.18), inset 0 2px 0 rgba(255,255,255,0.4)`,
                          textShadow: "0 1px 0 rgba(255,255,255,0.3)",
                        }}
                        data-testid="grande-final-cta"
                      >
                        {lang === "en" ? "PLAY" : lang === "es" ? "JUGAR" : "JOGAR"}
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── SECONDARY compact block (opcional) — abaixo do bloco principal
              em desktop; em mobile renderizado como sibling APÓS phases-block. ── */}
          {!isMobile && cfg.online_secondary_section?.enabled && (() => {
            const sec = cfg.online_secondary_section || {};
            // Scheduling: hide if the current time is outside [visible_from, visible_until]
            const now = Date.now();
            if (sec.visible_from) {
              const from = Date.parse(sec.visible_from);
              if (!isNaN(from) && now < from) return null;
            }
            if (sec.visible_until) {
              const until = Date.parse(sec.visible_until);
              if (!isNaN(until) && now > until) return null;
            }
            const scale = Math.max(0.4, Math.min(1, (sec.scale ?? 65) / 100));
            const secCards = sec.inherit_cards ? (cfg.online_section?.cards || []) : (sec.cards || []);
            const palette = [
              { neon: "#3DA5FF", deep: "#0A1B45", icon: Clock },
              { neon: "#39E635", deep: "#0A2E12", icon: Bot   },
              { neon: "#FF1A8C", deep: "#3A0C28", icon: Users },
              { neon: "#FFD93D", deep: "#3A2E00", icon: Clock },
            ];
            return (
              <div className="relative" data-testid="online-secondary-wrapper" style={{ marginTop: sec.margin_top ?? 16 }}>
                {/* Painted title banner (e.g. "APRENDER A JOGAR") — same mechanics as phases/online sections */}
                {pick(sec, "title", lang) && (
                  <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10" style={{ minWidth: 220 }}>
                    <PaintedTitleBanner
                      title={pick(sec, "title", lang)}
                      paintColor={sec.banner_paint_color || "#1B6FD1"}
                      splatterColor={sec.banner_splatter_color || "#FFD93D"}
                      showSplatter={sec.banner_show_splatter !== false}
                      width={sec.banner_width || undefined}
                      height={sec.banner_height || undefined}
                      offsetX={sec.banner_offset_x || 0}
                      offsetY={sec.banner_offset_y || 0}
                      scale={sec.banner_scale ?? 1}
                      roughness={sec.banner_roughness ?? 10}
                      paddingX={sec.banner_padding_x ?? 40}
                      paddingY={sec.banner_padding_y ?? 10}
                      uid="online-secondary"
                    />
                  </div>
                )}
                <div
                  className="relative overflow-hidden"
                  style={{
                    marginTop: pick(sec, "title", lang) ? 24 : 0,
                    background: sec.container_bg ? `linear-gradient(180deg, ${sec.container_bg} 0%, ${sec.container_bg} 100%)` : "linear-gradient(180deg,#030B2A 0%,#07194D 100%)",
                    border: `2px solid ${sec.container_border || "#3DA5FF"}`,
                    borderRadius: sec.container_radius ?? 22,
                    padding: pick(sec, "title", lang) ? "34px 12px 14px" : "14px 12px",
                    boxShadow: `0 0 18px ${sec.container_glow_color || "#3DA5FF"}${Math.round((sec.container_glow_intensity ?? 30) * 2.55).toString(16).padStart(2, "0")}, 0 12px 30px -10px rgba(0,0,0,0.55)`,
                    contain: "layout paint",
                  }}
                  data-testid="online-secondary-block"
                >
                {/* Seasonal particles overlay (only when scheduler is within window) */}
                <SeasonalOverlay effect={sec.seasonal_effect} intensity={sec.seasonal_intensity ?? 60} />

                {sec.video_mode ? (
                  /* ── Video mode: overrides cards ── */
                  <div style={{ marginTop: sec.video_offset_y || 0, marginBottom: sec.video_bottom_gap || 0 }}>
                    <PromoVideo
                      url={pick(sec, "video_url", lang) || sec.video_url}
                      poster={pick(sec, "video_poster", lang) || sec.video_poster}
                      autoplay={sec.video_autoplay !== false}
                      muted={sec.video_muted !== false}
                      loop={sec.video_loop !== false}
                      controls={sec.video_controls !== false}
                      aspect={sec.video_aspect || "16/9"}
                    />
                  </div>
                ) : (
                <div
                  className="grid"
                  style={{
                    gap: sec.card_gap ?? 10,
                    gridTemplateColumns: `repeat(${secCards.length || 1}, minmax(0, 1fr))`,
                  }}
                >
                  {secCards.map((c, idx) => {
                    const P = palette[idx % palette.length];
                    const Icon = P.icon;
                    const iconSize = Math.round(56 * scale);
                    const titleSize = Math.round(22 * scale);
                    const descSize = Math.round(14 * scale);
                    const btnH = Math.round(42 * scale);
                    const btnFont = Math.round(16 * scale);
                    return (
                      <div
                        key={`sec-${c.id}-${idx}`}
                        className="relative overflow-hidden flex flex-col items-center text-center"
                        style={{
                          borderRadius: Math.round((cfg.online_section?.card_radius || 22) * scale),
                          padding: `${Math.round(18 * scale)}px ${Math.round(10 * scale)}px`,
                          background: `linear-gradient(180deg, ${P.deep} 0%, rgba(13,18,55,0.85) 55%, ${P.neon}22 100%)`,
                          border: `2px solid ${P.neon}`,
                          boxShadow: `0 0 18px ${P.neon}55, inset 0 0 20px ${P.neon}22`,
                          contain: "layout paint",
                        }}
                        data-testid={`online-secondary-card-${c.id}-${idx}`}
                      >
                        {c.icon_url ? (
                          <img src={c.icon_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${c.icon_url}` : c.icon_url} alt="" loading="lazy" decoding="async" style={{ width: iconSize, height: iconSize, marginBottom: 8, filter: `drop-shadow(0 0 12px ${P.neon}AA)` }} />
                        ) : c.emoji_override ? (
                          <span aria-hidden style={{ fontSize: iconSize, lineHeight: 1, marginBottom: 8, filter: `drop-shadow(0 0 12px ${P.neon}AA)` }}>{c.emoji_override}</span>
                        ) : (
                          <Icon style={{ width: iconSize, height: iconSize, color: P.neon, strokeWidth: 2, marginBottom: 8, filter: `drop-shadow(0 0 12px ${P.neon}AA)` }} aria-hidden />
                        )}
                        <div className="font-[Fredoka] font-black uppercase" style={{ color: P.neon, fontSize: Math.round(titleSize * 0.6), lineHeight: 1, marginBottom: 2 }}>VS</div>
                        <div className="font-[Fredoka] font-black uppercase tracking-wide leading-tight" style={{ color: P.neon, fontSize: titleSize, textShadow: `0 0 8px ${P.neon}AA`, marginBottom: 8 }}>
                          {pick(c, "title", lang).replace(/^VS\s+/i, "")}
                        </div>
                        {sec.show_descriptions !== false && (
                          <p className="leading-snug flex-1" style={{ color: "rgba(255,255,255,0.9)", fontSize: descSize, marginBottom: 8 }}>
                            {pick(c, "description", lang)}
                          </p>
                        )}
                        <Link
                          to={c.url || "/"}
                          className="font-[Fredoka] font-black uppercase tracking-wider transition hover:scale-105 grid place-items-center"
                          style={{
                            width: (sec.card_button_w ?? 0) > 0 ? Math.round((sec.card_button_w ?? 140) * scale) : "100%",
                            height: Math.round((sec.card_button_h ?? 42) * scale),
                            borderRadius: Math.round((sec.card_button_h ?? 42) * scale) / 2,
                            background: P.neon,
                            color: "#FFFFFF",
                            fontSize: Math.round((sec.card_button_size ?? 16) * scale),
                            boxShadow: `0 0 14px ${P.neon}`,
                          }}
                          data-testid={`online-secondary-card-${c.id}-${idx}-cta`}
                        >
                          {pick(c, "button", lang)}
                        </Link>
                      </div>
                    );
                  })}
                </div>
                )}
                <LearnLinkButton sec={sec} lang={lang} />
              </div>
              </div>
            );
          })()}
        </div>

        {/* MOBILE-ONLY: online-secondary rendered as its own grid item so
            with CSS order:3 it appears AFTER online-block cards AND after
            phases-block per user reference. Video mode only (card mode falls
            back to the desktop-inside render). */}
        {isMobile && cfg.online_secondary_section?.enabled && (() => {
          const sec = cfg.online_secondary_section || {};
          const now = Date.now();
          if (sec.visible_from) {
            const from = Date.parse(sec.visible_from);
            if (!isNaN(from) && now < from) return null;
          }
          if (sec.visible_until) {
            const until = Date.parse(sec.visible_until);
            if (!isNaN(until) && now > until) return null;
          }
          return (
            <div
              className="relative"
              data-testid="online-secondary-wrapper-mobile"
              style={{ marginTop: 12 }}
            >
              {pick(sec, "title", lang) && (
                <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10" style={{ minWidth: 220, textAlign: "center" }}>
                  <PaintedTitleBanner
                    title={pick(sec, "title", lang)}
                    paintColor={sec.banner_paint_color || "#1B6FD1"}
                    splatterColor={sec.banner_splatter_color || "#FFD93D"}
                    showSplatter={sec.banner_show_splatter !== false}
                    scale={sec.banner_scale ?? 1}
                    roughness={sec.banner_roughness ?? 10}
                    paddingX={sec.banner_padding_x ?? 40}
                    paddingY={sec.banner_padding_y ?? 10}
                    uid="online-secondary-mobile"
                  />
                </div>
              )}
              <div
                className="relative overflow-hidden"
                style={{
                  marginTop: pick(sec, "title", lang) ? 24 : 0,
                  background: sec.container_bg ? `linear-gradient(180deg, ${sec.container_bg} 0%, ${sec.container_bg} 100%)` : "linear-gradient(180deg,#030B2A 0%,#07194D 100%)",
                  border: `2px solid ${sec.container_border || "#3DA5FF"}`,
                  borderRadius: sec.container_radius ?? 22,
                  padding: pick(sec, "title", lang) ? "56px 12px 14px" : "14px 12px",
                  boxShadow: `0 0 18px ${sec.container_glow_color || "#3DA5FF"}${Math.round((sec.container_glow_intensity ?? 30) * 2.55).toString(16).padStart(2, "0")}, 0 12px 30px -10px rgba(0,0,0,0.55)`,
                  contain: "layout paint",
                }}
              >
                <SeasonalOverlay effect={sec.seasonal_effect} intensity={sec.seasonal_intensity ?? 60} />
                {sec.video_mode && (
                  <div style={{ marginTop: sec.video_offset_y || 0, marginBottom: sec.video_bottom_gap || 0 }}>
                    <PromoVideo
                      url={pick(sec, "video_url", lang) || sec.video_url}
                      poster={pick(sec, "video_poster", lang) || sec.video_poster}
                      autoplay={sec.video_autoplay !== false}
                      muted={sec.video_muted !== false}
                      loop={sec.video_loop !== false}
                      controls={sec.video_controls !== false}
                      aspect={sec.video_aspect || "16/9"}
                    />
                  </div>
                )}
                <LearnLinkButton sec={sec} lang={lang} />
              </div>
            </div>
          );
        })()}

      </div>

      {/* GRANDE FINAL — the dedicated card now lives INSIDE the blue
          "online-secondary-block" container (see injection above). The
          external standalone button was removed in Feb-2026 after the
          user asked for the card to match the VS TEMPO/ROBOT/HUMANO
          visual language within the same bordered area. */}

      {/* Enrollment deadline + inscricoes CTA + countdown — FULL-PAGE-WIDTH row.
          Renders OUTSIDE the phases/online grid so `left | center | right` presets align relative to the entire page width, not just the phases column. */}
      {(() => {
        const ps = cfg.phases_section || {};
        const deadlineLabel = pick(ps, "deadline_label", lang);
        const deadlineUrl = pick(ps, "deadline_url", lang) || ps.deadline_url || "";
        const inscricoesLabel = pick(ps, "inscricoes_label", lang);
        const inscricoesUrl = pick(ps, "inscricoes_url", lang) || "";
        const storeLabel = pick(ps, "store_label", lang);
        const storeUrl = pick(ps, "store_url", lang) || "";
        const showDeadline = ps.deadline_show !== false && !!deadlineLabel;
        const showInscricoes = ps.inscricoes_show !== false && (inscricoesLabel || inscricoesUrl);
        const showCountdown = ps.countdown_show !== false && ps.deadline_target_date;
        const showStore = ps.store_show === true && (storeLabel || storeUrl);
        if (!showDeadline && !showInscricoes && !showCountdown && !showStore) return null;
        const align = ps.deadline_row_align || "center";
        const justify = align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center";
        return (
          <div className="max-w-7xl mx-auto px-6">
            <div
              className={`flex flex-wrap items-center gap-3 ${justify}`}
              style={{
                marginTop: ps.deadline_margin_top ?? 24,
                transform: `translate(${ps.deadline_row_offset_x || 0}px, ${ps.deadline_row_offset_y || 0}px)`,
              }}
              data-testid="enrollment-deadline-row"
            >
              {showDeadline && (() => {
                const commonStyle = {
                  background: ps.deadline_bg || "linear-gradient(90deg, #FFD93D 0%, #FFA800 100%)",
                  color: ps.deadline_color || "#0A0E2A",
                  boxShadow: "0 4px 18px rgba(255,168,0,0.35), inset 0 1px 0 rgba(255,255,255,0.5)",
                  border: "2px solid rgba(255,255,255,0.35)",
                };
                const inner = (
                  <>
                    <span aria-hidden style={{ fontSize: 18 }}>📅</span>
                    <span
                      className="font-[Fredoka] font-black uppercase tracking-wide text-center"
                      style={{ fontSize: ps.deadline_size || 16 }}
                    >
                      {deadlineLabel}
                    </span>
                  </>
                );
                return deadlineUrl ? (
                  <a
                    href={deadlineUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full px-5 py-2.5 no-underline transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-white/60"
                    style={commonStyle}
                    data-testid="enrollment-deadline"
                  >
                    {inner}
                  </a>
                ) : (
                  <div
                    className="flex items-center gap-2 rounded-full px-5 py-2.5"
                    style={commonStyle}
                    data-testid="enrollment-deadline"
                  >
                    {inner}
                  </div>
                );
              })()}

              {showInscricoes && (() => {
                const btnStyle = {
                  background: ps.inscricoes_bg || "linear-gradient(90deg, #22C55E 0%, #15803D 100%)",
                  color: ps.inscricoes_color || "#FFFFFF",
                  boxShadow: "0 4px 18px rgba(34,197,94,0.45), inset 0 1px 0 rgba(255,255,255,0.35)",
                  border: "2px solid rgba(255,255,255,0.35)",
                };
                const inner = (
                  <>
                    <span aria-hidden style={{ fontSize: 18 }}>✏️</span>
                    <span
                      className="font-[Fredoka] font-black uppercase tracking-wide text-center"
                      style={{ fontSize: ps.inscricoes_size || 16 }}
                    >
                      {inscricoesLabel || "INSCRIÇÕES"}
                    </span>
                  </>
                );
                return inscricoesUrl ? (
                  <a
                    href={inscricoesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full px-5 py-2.5 no-underline transition-transform hover:scale-[1.05] focus:outline-none focus:ring-2 focus:ring-white/60"
                    style={btnStyle}
                    data-testid="enrollment-cta"
                  >
                    {inner}
                  </a>
                ) : (
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-full px-5 py-2.5 cursor-default"
                    style={btnStyle}
                    data-testid="enrollment-cta"
                    disabled
                  >
                    {inner}
                  </button>
                );
              })()}

              {showCountdown && (() => {
                const target = new Date(`${ps.deadline_target_date}T23:59:59`);
                const now = new Date();
                const days = Math.ceil((target.getTime() - now.getTime()) / 86400000);
                if (!Number.isFinite(days) || days < 0) return null;
                const label = pick(ps, "countdown_label", lang) || "FALTAM";
                const suffix = pick(ps, "countdown_suffix", lang) || "DIAS";
                return (
                  <div
                    className="flex items-center gap-2 rounded-full px-5 py-2.5"
                    style={{
                      background: ps.countdown_bg || "linear-gradient(90deg, #3DA5FF 0%, #6E5BFF 100%)",
                      color: ps.countdown_color || "#FFFFFF",
                      boxShadow: "0 4px 18px rgba(61,165,255,0.35), inset 0 1px 0 rgba(255,255,255,0.35)",
                      border: "2px solid rgba(255,255,255,0.35)",
                    }}
                    data-testid="enrollment-countdown"
                  >
                    <span aria-hidden style={{ fontSize: 18 }}>⏳</span>
                    <span
                      className="font-[Fredoka] font-black uppercase tracking-wide text-center"
                      style={{ fontSize: ps.countdown_size || 16 }}
                    >
                      {label} {days} {suffix}
                    </span>
                  </div>
                );
              })()}

              {showStore && (() => {
                // Store button — external link to the online shop. Rendered
                // via the same pill primitive as the other banner items so
                // gap + auto-centering are handled by the parent flex row.
                const btnStyle = {
                  background: ps.store_bg || "linear-gradient(90deg, #FF6A00 0%, #EE0979 100%)",
                  color: ps.store_color || "#FFFFFF",
                  boxShadow: "0 4px 18px rgba(238,9,121,0.45), inset 0 1px 0 rgba(255,255,255,0.35)",
                  border: "2px solid rgba(255,255,255,0.35)",
                };
                const icon = ps.store_icon || "🛒";
                const inner = (
                  <>
                    <span
                      className="font-[Fredoka] font-black uppercase tracking-wide text-center"
                      style={{ fontSize: ps.store_size || 16 }}
                    >
                      {storeLabel || "STORE"}
                    </span>
                    {icon && <span aria-hidden style={{ fontSize: 20 }}>{icon}</span>}
                  </>
                );
                return storeUrl ? (
                  <a
                    href={storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full px-5 py-2.5 no-underline transition-transform hover:scale-[1.05] focus:outline-none focus:ring-2 focus:ring-white/60"
                    style={btnStyle}
                    data-testid="enrollment-store"
                  >
                    {inner}
                  </a>
                ) : (
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-full px-5 py-2.5 cursor-default"
                    style={btnStyle}
                    data-testid="enrollment-store"
                    disabled
                  >
                    {inner}
                  </button>
                );
              })()}
            </div>
          </div>
        );
      })()}
    </section>
  );
}

/* =========================================================================
   STATS FOOTER — dark banner with counters + tagline + social icons
   ========================================================================= */
export const StatsFooter = React.memo(function StatsFooter({ cfg, lang }) {
  const fs = cfg.footer_stats || {};
  const items = (fs.items || []).filter((s) => s?.enabled !== false);
  const social = fs.social_links || {};
  const tagline = pick(fs, "tagline", lang);
  const taglineHighlight = pick(fs, "tagline_highlight", lang);
  const highlightColor = fs.tagline_highlight_color || "#FFD400";
  const footerRef = React.useRef(null);
  // Dynamic marginTop — anchors the FAIXA immediately below the last active
  // reference block (secondary compact block if visible, else online block).
  // Guarantees no overlap regardless of which "Presets Globais de Layout"
  // the admin applies, since we always measure the ACTUAL DOM bottom.
  // Uses a functional setter (cumulative correction) so it converges in one
  // step and doesn't oscillate.
  const [autoOffset, setAutoOffset] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const compute = () => {
      // Reference blocks the FAIXA must sit BELOW (never overlap). We take
      // the LATEST bottom across:
      //   - `online-secondary-block`     (primary reference, per PRD)
      //   - `online-block`               (fallback if secondary is hidden)
      //   - `phases-block`               (safety-net so left column never bleeds under)
      //   - `enrollment-deadline-row`    (deadline + CTA + countdown pills — sit below phases)
      const candidates = [
        document.querySelector('[data-testid="online-secondary-block"]'),
        document.querySelector('[data-testid="online-block"]'),
        document.querySelector('[data-testid="phases-block"]'),
        document.querySelector('[data-testid="enrollment-deadline-row"]'),
      ].filter(Boolean);
      if (!candidates.length) return;
      const refBottom = Math.max(...candidates.map((n) => n.getBoundingClientRect().bottom + window.scrollY));
      const footerTop = el.getBoundingClientRect().top + window.scrollY;
      // Clamp admin's fine-tune to non-negative — the FAIXA MUST NEVER
      // overlap the reference block regardless of admin config or preset.
      const extraGap = Math.max(0, fs.section_margin_top ?? 0);
      const targetTop = refBottom + extraGap;
      const correction = targetTop - footerTop;
      // Skip micro-corrections to prevent infinite RAF loops.
      if (Math.abs(correction) < 1) return;
      setAutoOffset((prev) => prev + correction);
    };
    compute();
    const ro = new ResizeObserver(compute);
    document.querySelectorAll('[data-testid="online-block"],[data-testid="online-secondary-block"],[data-testid="phases-block"],[data-testid="enrollment-deadline-row"]').forEach((n) => ro.observe(n));
    window.addEventListener("resize", compute);
    // Recompute after fonts/images/videos load
    const t1 = setTimeout(compute, 300);
    const t2 = setTimeout(compute, 1200);
    return () => { ro.disconnect(); window.removeEventListener("resize", compute); clearTimeout(t1); clearTimeout(t2); };
  }, [cfg, fs.section_margin_top]);
  // Container knobs
  const bgFrom = fs.container_bg_from || "#02071E";
  const bgTo = fs.container_bg_to || "#071B52";
  const borderColor = fs.container_border_color || "#3DA5FF";
  const borderOpacity = (fs.container_border_opacity ?? 45) / 100;
  const borderRadius = fs.container_border_radius ?? 22;
  const minHeight = fs.container_min_height ?? 128;
  const padX = fs.container_padding_x ?? 22;
  const padY = fs.container_padding_y ?? 16;
  const glowColor = fs.container_glow_color || "#3DA5FF";
  const glowIntensity = fs.container_glow_intensity ?? 18;
  const showStripes = fs.show_purple_stripes !== false;
  const stripesColor = fs.purple_stripes_color || "#9B4DFF";
  // Separators
  const sepColor = fs.separator_color || "#3DA5FF";
  const sepOpacity = (fs.separator_opacity ?? 30) / 100;
  const sepH = fs.separator_height ?? 76;
  // Stat block
  const valSize = fs.stat_value_size ?? 30;
  const valColor = fs.stat_value_color || "#FFFFFF";
  const labelSize = fs.stat_label_size ?? 13;
  const labelColor = fs.stat_label_color || "#FFFFFF";
  const labelOpacity = (fs.stat_label_opacity ?? 85) / 100;
  const iconSize = fs.stat_icon_size ?? 72;
  const statGap = fs.stat_gap ?? 12;
  // Pill
  const pillBgColor = fs.pill_bg_color || "#02071E";
  const pillBgOpacity = (fs.pill_bg_opacity ?? 60) / 100;
  const pillBorderColor = fs.pill_border_color || "#3DA5FF";
  const pillBorderOpacity = (fs.pill_border_opacity ?? 55) / 100;
  const pillRadius = fs.pill_border_radius ?? 46;
  const pillMinH = fs.pill_min_height ?? 92;
  const tagSize = fs.tagline_size ?? 15;
  const tagColor = fs.tagline_color || "#FFFFFF";
  const tagHighlightSize = fs.tagline_highlight_size ?? 20;
  // Social
  const socialSize = fs.social_size ?? 60;
  const socialGap = fs.social_gap ?? 10;
  // Fallback icon emoji when item has none
  const DEFAULT_META = {
    paises:      { emoji: "🌐", bg: "#1B6FD1", glow: "#3DA5FF" },
    alunos:      { emoji: "😊", bg: "#F2B500", glow: "#FFD93D" },
    escolas:     { emoji: "🏫", bg: "#E04017", glow: "#FF6B35" },
    disciplinas: { emoji: "🃏", bg: "#C8133E", glow: "#FF1A5C" },
  };
  // Social network platforms with branded colors + visibility flags.
  const SOCIAL = [
    { id: "youtube",   Icon: Youtube,   color: "#FF0000", glow: "#FF3333", visible: fs.social_show_youtube !== false },
    { id: "instagram", Icon: Instagram, color: "#E1306C", glow: "#E1306C", visible: fs.social_show_instagram !== false, gradient: "linear-gradient(135deg,#FFD600 0%,#FF7A00 30%,#FF0069 60%,#D300C5 100%)" },
    { id: "facebook",  Icon: Facebook,  color: "#1877F2", glow: "#3DA5FF", visible: fs.social_show_facebook !== false },
    { id: "tiktok",    Icon: null,      color: "#000000", glow: "#FF0050", visible: fs.social_show_tiktok !== false, glyph: "TT" },
  ];
  // Hex → rgba helper for border/pill/separator opacity control.
  const rgba = (hex, a) => {
    const h = String(hex || "#000").replace("#", "");
    const n = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  };
  return (
    <footer
      ref={footerRef}
      className="relative px-4 sm:px-6"
      style={{
        marginTop: autoOffset,
        paddingTop: fs.section_padding_top ?? 8,
        paddingBottom: fs.section_padding_bottom ?? 8,
        zIndex: 100,
      }}
      data-testid="stats-footer"
    >
      <div
        className="mx-auto relative flex items-center gap-4 overflow-hidden"
        style={{
          maxWidth: fs.container_max_width ?? 1280,
          minHeight,
          padding: `${padY}px ${padX}px`,
          background: `linear-gradient(180deg, ${bgFrom} 0%, ${bgTo} 100%)`,
          border: `1px solid ${rgba(borderColor, borderOpacity)}`,
          borderRadius,
          boxShadow: `0 0 ${glowIntensity + 6}px ${rgba(glowColor, 0.18)}, 0 0 ${glowIntensity * 2 + 12}px ${rgba(glowColor, 0.08)}, 0 12px 32px -10px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)`,
        }}
      >
        {/* Decorative diagonal purple strokes on the far left */}
        {showStripes && (
          <div
            className="absolute left-0 top-0 bottom-0 pointer-events-none"
            style={{
              width: 24,
              background: `repeating-linear-gradient(135deg, ${rgba(stripesColor, 0.6)} 0px, ${rgba(stripesColor, 0.6)} 2px, transparent 2px, transparent 8px)`,
              opacity: 0.55,
            }}
            aria-hidden
          />
        )}

        {/* ── Stats blocks (4) ── */}
        {items.map((s, i) => {
          const fallback = DEFAULT_META[s.id] || { emoji: "✨", bg: "#1B6FD1", glow: "#3DA5FF" };
          const bg = s.bg_color || fallback.bg;
          const glow = s.glow_color || fallback.glow;
          const emoji = s.emoji || fallback.emoji;
          return (
            <React.Fragment key={s.id || i}>
              <div className="flex items-center flex-shrink-0" style={{ gap: statGap }} data-testid={`stat-${s.id || i}`}>
                {/* Coloured circular icon */}
                <div
                  className="rounded-full grid place-items-center flex-shrink-0"
                  style={{
                    width: iconSize,
                    height: iconSize,
                    fontSize: iconSize * 0.55,
                    lineHeight: 1,
                    background: bg,
                    boxShadow: `0 0 22px ${rgba(glow, 0.6)}, inset 0 -4px 0 rgba(0,0,0,0.22), inset 0 2px 0 rgba(255,255,255,0.32)`,
                  }}
                >
                  {emoji}
                </div>
                {/* Value + label */}
                <div className="leading-tight">
                  <div className="font-[Fredoka] font-black" style={{ color: valColor, fontSize: valSize, textShadow: `0 0 10px ${rgba(glow, 0.4)}, 0 2px 0 rgba(0,0,0,0.4)` }}>
                    {s.value}
                  </div>
                  <div className="uppercase tracking-wider font-bold" style={{ color: rgba(labelColor, labelOpacity), fontSize: labelSize, letterSpacing: "0.08em" }}>
                    {pick(s, "label", lang)}
                  </div>
                </div>
              </div>
              {/* Vertical separator after each block (except last stat) */}
              {i < items.length - 1 && (
                <div aria-hidden style={{ width: 1, height: sepH, background: sepColor, opacity: sepOpacity, flexShrink: 0 }} />
              )}
            </React.Fragment>
          );
        })}

        {/* Separator before CTA */}
        {items.length > 0 && (
          <div aria-hidden style={{ width: 1, height: sepH, background: sepColor, opacity: sepOpacity, flexShrink: 0 }} />
        )}

        {/* ── CTA central pill block — 2 lines with yellow highlight on 2nd line ── */}
        <div
          className="flex flex-col items-center justify-center text-center flex-1 min-w-0"
          style={{
            minHeight: pillMinH,
            padding: "10px 22px",
            background: rgba(pillBgColor, pillBgOpacity),
            border: `1px solid ${rgba(pillBorderColor, pillBorderOpacity)}`,
            borderRadius: pillRadius,
            boxShadow: `inset 0 0 14px ${rgba(pillBorderColor, 0.22)}`,
          }}
          data-testid="footer-cta"
        >
          <div
            className="font-[Fredoka] font-black uppercase leading-tight"
            style={{ color: tagColor, fontSize: tagSize, letterSpacing: "0.04em" }}
          >
            {tagline}
          </div>
          {taglineHighlight && (
            <div
              className="font-[Fredoka] font-black uppercase leading-tight mt-1"
              style={{
                color: highlightColor,
                fontSize: tagHighlightSize,
                letterSpacing: "0.04em",
                textShadow: `0 0 12px ${rgba(highlightColor, 0.5)}, 0 2px 0 rgba(0,0,0,0.4)`,
              }}
              data-testid="footer-tagline-highlight"
            >
              {taglineHighlight}
            </div>
          )}
        </div>

        {/* Separator before socials */}
        <div aria-hidden style={{ width: 1, height: sepH, background: sepColor, opacity: sepOpacity, flexShrink: 0 }} />

        {/* ── Social icons block — always render visible entries (fallback URL = #) ── */}
        <div className="flex items-center flex-shrink-0" style={{ gap: socialGap }} data-testid="footer-socials">
          {SOCIAL.filter((sp) => sp.visible).map((sp) => {
            const url = social[sp.id] || "#";
            return (
              <a
                key={sp.id}
                href={url}
                target={url !== "#" ? "_blank" : undefined}
                rel="noreferrer"
                className="rounded-full grid place-items-center hover:scale-110 transition"
                style={{
                  width: socialSize,
                  height: socialSize,
                  background: sp.gradient || sp.color,
                  boxShadow: `0 0 16px ${rgba(sp.glow, 0.67)}, inset 0 -3px 0 rgba(0,0,0,0.25), inset 0 2px 0 rgba(255,255,255,0.3)`,
                  border: "1px solid rgba(255,255,255,0.15)",
                }}
                data-testid={`social-${sp.id}`}
              >
                {sp.Icon ? <sp.Icon style={{ width: socialSize * 0.45, height: socialSize * 0.45, color: "#FFFFFF" }} /> : <span className="text-white font-black" style={{ fontSize: socialSize * 0.3 }}>{sp.glyph}</span>}
              </a>
            );
          })}
        </div>
      </div>

      {/* ── Legal strip (Termos · Privacidade · Reclamações · Copyright) ──
          Sits directly below the stats bar, always on a single line on
          desktop. Fields editable per PT/EN/ES from the admin. */}
      {fs.legal_show !== false && (
        <div
          className="mx-auto mt-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-[11px] text-slate-300/80"
          style={{ maxWidth: fs.container_max_width ?? 1280 }}
          data-testid="legal-strip"
        >
          {fs.legal_terms_enabled !== false && pick(fs, "legal_terms", lang) && (
            <a href={fs.legal_terms_url || "#"} className="hover:text-lime-300 transition" data-testid="legal-terms">
              {pick(fs, "legal_terms", lang)}
            </a>
          )}
          {fs.legal_privacy_enabled !== false && pick(fs, "legal_privacy", lang) && (
            <a href={fs.legal_privacy_url || "#"} className="hover:text-lime-300 transition" data-testid="legal-privacy">
              {pick(fs, "legal_privacy", lang)}
            </a>
          )}
          {fs.legal_complaints_enabled !== false && pick(fs, "legal_complaints", lang) && (
            <a href={fs.legal_complaints_url || "#"} target="_blank" rel="noreferrer" className="hover:text-lime-300 transition" data-testid="legal-complaints">
              {pick(fs, "legal_complaints", lang)}
            </a>
          )}
          {fs.legal_copyright_enabled !== false && pick(fs, "legal_copyright", lang) && (
            <span className="text-slate-400/70" data-testid="legal-copyright">{pick(fs, "legal_copyright", lang)}</span>
          )}
        </div>
      )}
    </footer>
  );
});


/* =========================================================================
   SITE FOOTER — multi-column footer below the StatsFooter, inspired by
   the Natalmático/Eudactica layout but with the SUPERTMATIK lime + indigo
   palette. Logo + description (col 1), 3 link columns (col 2-4), bottom
   row with contacts + copyright + "Made with Emergent" badge.
   ========================================================================= */
const SiteFooter = React.memo(function SiteFooter({ cfg, lang }) {
  const f = cfg.site_footer || {};
  const cols = f.columns || [];
  const desc = pick(f, "description", lang);
  const addr = pick(f, "contact_address", lang);
  const copy = pick(f, "copyright", lang);
  return (
    <footer className="bg-slate-950 text-slate-300 relative overflow-hidden" data-testid="site-footer">
      {/* Soft lime glow band at the top edge for visual continuity with StatsFooter */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-lime-400/60 to-transparent" />
      <div className="absolute -top-20 left-1/4 w-80 h-80 bg-lime-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 pt-14 pb-8 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Brand column */}
          <div className="lg:col-span-4">
            {cfg.logo_url ? (
              <img src={cfg.logo_url} alt="logo" className="h-10 object-contain mb-4 brightness-0 invert" loading="lazy" decoding="async" />
            ) : (
              <div className="text-2xl font-[Fredoka] font-black tracking-tight mb-4">
                <span className="text-white">SUPERT</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-br from-lime-400 to-emerald-400">MATIK★</span>
              </div>
            )}
            <p className="text-sm leading-relaxed text-slate-400 max-w-xs">{desc}</p>
            {/* Contact mini-list */}
            <div className="mt-6 space-y-2 text-xs">
              {f.contact_email && (
                <a href={`mailto:${f.contact_email}`} className="flex items-center gap-2 text-slate-300 hover:text-lime-400 transition" data-testid="footer-email">
                  <Mail className="w-4 h-4 text-lime-400" /> {f.contact_email}
                </a>
              )}
              {f.contact_phone && (
                <a href={`tel:${f.contact_phone}`} className="flex items-center gap-2 text-slate-300 hover:text-lime-400 transition" data-testid="footer-phone">
                  <Phone className="w-4 h-4 text-lime-400" /> {f.contact_phone}
                </a>
              )}
              {addr && (
                <div className="flex items-center gap-2 text-slate-400" data-testid="footer-address">
                  <MapPin className="w-4 h-4 text-lime-400" /> {addr}
                </div>
              )}
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6 lg:gap-8">
            {cols.map((col) => (
              <div key={col.id} data-testid={`footer-col-${col.id}`}>
                <h3 className="text-[11px] font-[Fredoka] font-black uppercase tracking-wider text-lime-400 mb-4 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> {pick(col, "title", lang)}
                </h3>
                <ul className="space-y-2.5">
                  {(col.links || []).map((l, idx) => (
                    <li key={l.url || `footer-${col.id}-${idx}`}>
                      <Link
                        to={l.url || "/"}
                        className="text-sm text-slate-300 hover:text-lime-400 hover:translate-x-0.5 inline-block transition-all"
                        data-testid={`footer-link-${col.id}-${idx}`}
                      >
                        {pick(l, "label", lang)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom row: copyright + Made with Emergent */}
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div className="text-slate-500" data-testid="footer-copyright">{copy}</div>
          <a
            href="https://app.emergent.sh/?utm_source=emergent-badge"
            target="_blank"
            rel="noreferrer"
            className="text-slate-500 hover:text-lime-400 transition inline-flex items-center gap-1.5"
          >
            <span className="text-[10px] uppercase tracking-wider">Made with</span>
            <span className="text-lime-400 font-bold">Emergent</span>
          </a>
        </div>
      </div>
    </footer>
  );
});
